import { TimesheetWarnings } from '@/components/TimesheetWarnings';
import { TimesheetMaterials } from "@/components/MaterialUsagePicker";
import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { todayIso, nzTodayIso, shiftIsoDay } from "@/lib/dates";
import { PageHeader, StatusBadge, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceNoteButton } from "@/components/VoiceNoteButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Check, X, Trash2, Clock, Download, Search, FileText,
  Truck, Wrench, Briefcase, CheckCircle2, Coffee, ChevronDown, ChevronRight,
  Edit3, Send, FileEdit, Save, Gauge, UserCog, Camera, MoreHorizontal, Filter, ListChecks,
  XCircle, CalendarDays, MapPin, RotateCcw, Loader2, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import {
  ResponsiveContainer, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, PieChart, Pie, Cell, Area, ComposedChart,
} from "recharts";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DateField } from "@/components/DateField";
import { MoodPrompt } from "@/components/MoodPrompt";
import { uploadMany } from "@/lib/imageUpload";
import { UploadingThumb } from "@/components/UploadingThumb";
import { meterTypeFor } from "@/lib/meterType";
import { TimesheetInspectionPanel } from "@/components/TimesheetInspectionPanel";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Heuristic: vehicle vs machine icon based on vehicle_type
const VEHICLE_TYPES = new Set(["ute", "truck", "van", "car", "trailer", "pickup", "vehicle"]);
function assetIcon(t) {
  const v = (t || "").toLowerCase();
  return VEHICLE_TYPES.has(v) ? Truck : Wrench;
}

function dateBits(iso) {
  if (!iso) return { day: "—", month: "", dow: "" };
  const d = new Date(iso + "T00:00:00");
  return { day: d.getDate(), month: MONTH_NAMES[d.getMonth()], dow: DAY_NAMES[d.getDay()] };
}

function statusAccent(s) {
  if (s === "approved") return "bg-green-600";
  if (s === "rejected") return "bg-red-600";
  return "bg-yellow-500";
}

function prettyTableDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}


export default function Timesheets() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [warningFilter, setWarningFilter] = useState('all');
  const initialParams = new URLSearchParams(window.location.search);
  const [page,setPage]=useState(1),[pageSize,setPageSize]=useState(user.role==='field'?20:50),[pageData,setPageData]=useState(null),[busy,setBusy]=useState(false),[loadError,setLoadError]=useState('');
  const [workerId,setWorkerId]=useState(initialParams.get('worker')||''),[jobId,setJobId]=useState(initialParams.get('job_id')||''),[dayType,setDayType]=useState(''),[customStart,setCustomStart]=useState(''),[customEnd,setCustomEnd]=useState('');
  const [workers,setWorkers]=useState([]);
  const requestRef=useRef(null),requestVersion=useRef(0);

  const [jobs, setJobs] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [gear, setGear] = useState([]);
  const [open, setOpen] = useState(false);
  const [clockPrefill, setClockPrefill] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);  // draft timesheet being edited
  // Mood prompt — shown only after a field worker successfully submits their
  // OWN timesheet (skips when the office is editing or filing on-behalf).
  const [moodPromptFor, setMoodPromptFor] = useState(null);
  const isField = user.role === "field";
  const [filter, setFilterValue] = useState("all");
  const setFilter=v=>{setPage(1);setFilterValue(v);};
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [weekStats, setWeekStats] = useState(null);
  const [loaded, setLoaded] = useState(false);  // becomes true after first successful fetch
  const [reassigning, setReassigning] = useState(null);  // timesheet object being reassigned
  const [missing, setMissing] = useState(null);  // { date, breakdown, missing[], check_armed_after_5pm }
  const [weekSummary, setWeekSummary] = useState(null);
  const [missingOpen, setMissingOpen] = useState(false);
  const [range, setRangeValue] = useState("all");
  const setRange=v=>{setPage(1);setRangeValue(v);};
  const [fieldCounts, setFieldCounts] = useState(null);

  const isOffice = user.role !== "field";
  const loc = useLocation();
  const navr = useNavigate();
  const focusedId = new URLSearchParams(loc.search).get("record");
  const [focusedRecord,setFocusedRecord] = useState(null);
  const [focusedError,setFocusedError] = useState("");
  useEffect(() => {
    if (!focusedId) return;
    let active = true;
    setFocusedRecord(null);setFocusedError("");
    api.get(`/timesheet-records/${encodeURIComponent(focusedId)}`, {__flSkipReadCache:true}).then(({data})=>{if(active)setFocusedRecord(data);}).catch(()=>{if(active)setFocusedError("This timesheet could not be opened.");});
    return ()=>{active=false;};
  }, [isOffice,focusedId,pageData]);

  // Quick Add FAB deep-link: /app/timesheets?new=1 opens the entry dialog
  useEffect(() => {
    if (new URLSearchParams(loc.search).get("new") === "1") {
      const params = new URLSearchParams(loc.search);
      const validClock = params.get("clock") === "1" && /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") || "") && ["start_time", "end_time"].every(key => /^([01]\d|2[0-3]):[0-5]\d$/.test(params.get(key) || ""));
      setClockPrefill(validClock ? { date: params.get("date"), start_time: params.get("start_time"), end_time: params.get("end_time"), overnight: params.get("overnight") === "1" } : null);
      if (params.get("job_id")) setJobId(params.get("job_id"));
      setEditingDraft(null);
      setOpen(true);
      ["clock", "date", "start_time", "end_time", "overnight"].forEach(key => params.delete(key));
      params.delete("new");
      navr({ pathname: loc.pathname, search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line
  }, [loc.search]);

  useEffect(() => {
    const t = setTimeout(() => {setPage(1);setDebouncedSearch(search.trim());}, 250);
    return () => clearTimeout(t);
  }, [search]);

  const queryParams = () => ({warning:warningFilter,status:filter==='all'?undefined:filter,q:debouncedSearch||undefined,period:range,user_id:workerId||undefined,job_id:jobId||undefined,entry_type:dayType||undefined,start:range==='custom'?customStart||undefined:undefined,end:range==='custom'?customEnd||undefined:undefined});
  const resetFilters = ()=>{setWarningFilter('all');setFilter('all');setSearch('');setRange('all');setWorkerId('');setJobId('');setDayType('');setCustomStart('');setCustomEnd('');};
  const load = async () => {
    requestRef.current?.abort();const controller=new AbortController();requestRef.current=controller;const version=++requestVersion.current;
    setBusy(true);setLoadError('');setFieldCounts(null);setExpanded({});
    try {
      const {data}=await api.get('/timesheets/page',{params:{...queryParams(),page,page_size:pageSize},__flSkipReadCache:true,signal:controller.signal});
      if(version!==requestVersion.current||controller.signal.aborted)return;
      setItems(data.items);setPageData(data);setPage(data.page);setFieldCounts(data.summary.statuses);
    } catch(e){if(!controller.signal.aborted&&version===requestVersion.current){setItems([]);setPageData(null);setLoadError(e.response?.status === 422 ? (typeof e.response.data.detail === 'string' ? e.response.data.detail : 'Check your filters and date range.') : 'Matching records and totals are unavailable. Please try again.');}}
    finally{if(version===requestVersion.current&&!controller.signal.aborted){setBusy(false);setLoaded(true);}}
    if(isOffice)api.get('/timesheets/week-summary').then(r=>{if(version===requestVersion.current)setWeekSummary(r.data);}).catch(()=>{});
    if(!isOffice)api.get('/stats').then(r=>{if(version===requestVersion.current)setWeekStats(r.data);}).catch(()=>{});
  };
  // Background refresh that keeps scroll position: no busy skeleton swap, keeps expanded rows.
  const silentRefresh = async () => {
    const version=++requestVersion.current;
    try {
      const {data}=await api.get('/timesheets/page',{params:{...queryParams(),page,page_size:pageSize},__flSkipReadCache:true});
      if(version!==requestVersion.current)return;
      setItems(data.items);setPageData(data);setPage(data.page);setFieldCounts(data.summary.statuses);
    } catch { /* keep current list on a background refresh failure */ }
    if(isOffice)api.get('/timesheets/week-summary').then(r=>{if(version===requestVersion.current)setWeekSummary(r.data);}).catch(()=>{});
  };
  useEffect(()=>{load();return()=>requestRef.current?.abort();},[filter,debouncedSearch,range,page,pageSize,workerId,jobId,dayType,customStart,customEnd,warningFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{if(isOffice)api.get('/users').then(r=>setWorkers(r.data)).catch(()=>{});},[isOffice]);
  const toggleDetail=async id=>{
    if(expanded[id]){setExpanded(v=>({...v,[id]:false}));return;}
    const version=requestVersion.current;
    try{const {data}=await api.get(`/timesheet-records/${id}`, {__flSkipReadCache:true});if(version!==requestVersion.current)return;setItems(rows=>rows.map(r=>r.id===id?data:r));setExpanded(v=>({...v,[id]:true}));}catch{toast.error('Unable to open timesheet');}
  };
  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/fleet").then((r) => setFleet(r.data.filter((v) => v.status === "active")));
    api.get("/gear").then((r) => setGear(r.data || [])).catch(() => {});
  }, []);

  // Office "who hasn't logged today" — checked every 5 minutes.
  // Skipped entirely when the org turned the card off in dashboard settings.
  const [showMissing, setShowMissing] = useState(null);

  useEffect(() => {
    if (!isOffice) return;
    api.get("/org/ui-prefs")
      .then((r) => setShowMissing(r.data?.missing_timesheets !== false))
      .catch(() => setShowMissing(true));
  }, [isOffice]);
  useEffect(() => {
    if (!isOffice || showMissing !== true) return;
    let alive = true;
    const fetchMissing = () => {
      api.get("/timesheets/missing")
        .then((r) => { if (alive) setMissing(r.data); })
        .catch(() => {});
    };
    fetchMissing();
    const t = setInterval(fetchMissing, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [isOffice, showMissing]);

  const reviewLocks = useRef(new Set());
  const [reviewing, setReviewing] = useState({});
  const [rejecting, setRejecting] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const review = async (id, status, notes) => {
    if (reviewLocks.current.has(id)) return false;
    reviewLocks.current.add(id);
    setReviewing(current => ({ ...current, [id]: status }));
    try {
      const { data } = await api.patch(`/timesheets/${id}`, { status, review_notes: notes ?? null });
      setItems(rows => rows.map(row => row.id === id ? { ...row, ...data } : row));
      toast.success(status === "pending" ? "Timesheet restored to pending" : `Timesheet ${status}`);
      await silentRefresh();
      return true;
    } catch (error) {
      console.warn("Timesheet review failed", { status: error.response?.status });
      toast.error(status === "approved" ? "Could not approve timesheet. Try again." : status === "rejected" ? "Could not reject timesheet. Try again." : "Could not restore timesheet. Try again.");
      return false;
    } finally {
      reviewLocks.current.delete(id);
      setReviewing(current => { const next = { ...current }; delete next[id]; return next; });
    }
  };
  const openReject = (id) => {
    if (reviewLocks.current.has(id)) return;
    setRejectNote("");
    setRejecting(items.find(row => row.id === id));
  };
  const rejectingBusy = !!reviewing[rejecting?.id];
  const remove = async (id) => {
    if (!window.confirm("Delete this timesheet?")) return;
    await api.delete(`/timesheets/${id}`);
    load();
  };
  const submitDraft = async (id) => {
    try {
      const res = await api.post(`/timesheets/${id}/submit`);
      toast.success(res.data?.offline_queued || res.offline
        ? "Saved on your phone — we'll submit it automatically once you're back in reception"
        : "Timesheet submitted for review");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    }
  };
  const editDraft = async ts => {
    try{const {data}=await api.get(`/timesheet-records/${ts.id}`);setEditingDraft(data);setOpen(true);}catch{toast.error('Unable to open timesheet');}
  };

  const exportCsv = async () => {
    try {
      const params = { ...queryParams(), _ts: Date.now() };
      const res = await api.get("/timesheets/export.csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `timesheets_${todayIso()}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed. Check your filters and try again."); }
  };
  const exportPdf = async () => {
    try {
      const params = { ...queryParams(), _ts: Date.now() };
      const res = await api.get("/timesheets/export.pdf", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `timesheets_${todayIso()}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF export failed. Narrow the filters or use CSV for more than 5,000 rows."); }
  };

  const newEntryDialog = (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingDraft(null); setClockPrefill(null); } }}>
      {isOffice && (
      <DialogTrigger asChild>
        <Button onClick={() => setEditingDraft(null)}
                className="h-10 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-semibold"
                data-testid="new-timesheet-button">
          <Plus className="w-4 h-4 mr-2" /> New entry
        </Button>
      </DialogTrigger>
      )}
      {open && (
        <NewTimesheetForm
          key={editingDraft?.id || "new-entry"}
          jobs={jobs}
          fleet={fleet}
          gear={gear}
          initial={editingDraft}
          initialJobId={jobId}
          clockPrefill={editingDraft ? null : clockPrefill}
          onDone={(result) => {
            setOpen(false);
            setClockPrefill(null);
            setEditingDraft(null);
            load();
            if (isField && result?.submitted && !result?.onBehalf) {
              setMoodPromptFor({ date: result.date, timesheetId: result.id });
            }
          }} />
      )}
    </Dialog>
  );

  return (
    <div data-testid="timesheets-page" className="px-4 py-4 md:p-6 lg:p-10 w-full pb-24 lg:pb-10">
      {focusedId && <section className="border rounded-xl p-4 mb-5"><div className="flex justify-between gap-3 mb-3"><h2 className="font-semibold">Linked timesheet{focusedRecord ? ` · ${focusedRecord.user_name} · ${focusedRecord.date}` : ""}</h2><Button variant="ghost" onClick={()=>{const next=new URLSearchParams(loc.search);next.delete("record");navr({pathname:loc.pathname,search:next.toString()},{replace:true});}}>Close</Button></div>{focusedRecord ? <TimesheetInspectionPanel ts={focusedRecord} onBillingChange={load} /> : <p>{focusedError || "Loading timesheet…"}</p>}</section>}
      <MoodPrompt
        open={!!moodPromptFor}
        onClose={() => setMoodPromptFor(null)}
        date={moodPromptFor?.date}
        timesheetId={moodPromptFor?.timesheetId}
      />
      {isOffice ? (
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4" data-testid="ts-office-header" data-tour-id="ts-office-header">
          <div>

            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900 dark:text-white leading-none" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>Timesheets</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2">Review and approve crew hours.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" className="h-10 rounded-lg border-zinc-200 dark:border-[#33363B] bg-white dark:bg-[#101620] text-zinc-700 dark:text-zinc-200 font-semibold" onClick={exportCsv} data-testid="export-csv-button">
              <Download className="w-4 h-4 mr-2" /> Export matching CSV
            </Button>
            <Button variant="outline" className="h-10 rounded-lg border-zinc-200 dark:border-[#33363B] bg-white dark:bg-[#101620] text-zinc-700 dark:text-zinc-200 font-semibold" onClick={exportPdf} data-testid="export-pdf-button">
              <FileText className="w-4 h-4 mr-2" /> Export PDF
            </Button>
            {newEntryDialog}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-5 flex items-start justify-between gap-3" data-testid="ts-field-header">
            <h1 className="font-display font-black uppercase tracking-tight text-4xl leading-none pt-1">Timesheets</h1>
            <div className="flex items-center gap-2 shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button"
                          className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${
                            range !== "all"
                              ? "border-[#FF5F15] text-[#FF5F15]"
                              : "border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300"
                          }`}
                          data-testid="ts-field-filter-btn" aria-label="Date range filter">
                    <Filter className="w-[18px] h-[18px]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-lg text-[13px]">
                  {[["all", "All time"], ["this_week", "This week"], ["last_week", "Last week"], ["last_30", "Last 30 days"]].map(([k, label]) => (
                    <DropdownMenuItem key={k} onClick={() => setRange(k)} data-testid={`ts-range-${k}-field`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-2 ${range === k ? "bg-[#FF5F15]" : "bg-transparent"}`} />
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button type="button"
                      onClick={() => { setEditingDraft(null); setOpen(true); }}
                      className="w-11 h-11 rounded-full bg-[#FF5F15] hover:bg-[#E65100] text-white flex items-center justify-center shadow-lg shadow-[#FF5F15]/25 active:scale-95 transition-all"
                      data-testid="ts-field-add-fab" aria-label="New timesheet entry">
                <Plus className="w-6 h-6" strokeWidth={2.4} />
              </button>
            </div>
          </div>
          {newEntryDialog}
        </>
      )}

      {/* Weekly summary row (office) */}
      {isOffice && (
        <WeekSummaryRow
          week={weekSummary}
          missing={showMissing === true ? missing : null}
          onPending={() => setFilter("pending")}
          onOpenMissing={() => setMissingOpen(true)}
        />
      )}
      {isOffice && missing && (
        <MissingDetailDialog open={missingOpen} onClose={() => setMissingOpen(false)} data={missing} />
      )}

      {/* Field worker stats strip — mockup style */}
      {!isOffice && (
        <div className="mb-4 bg-[#101620] border border-zinc-800 rounded-2xl px-2 py-4 grid grid-cols-4 divide-x divide-zinc-800" data-testid="week-hours-strip">
          {[
            { icon: Clock, tone: "text-[#FF5F15]", value: (weekStats?.my_week_hours ?? 0).toFixed(1), label: "Hrs this week" },
            { icon: CheckCircle2, tone: "text-emerald-500", value: fieldCounts?.approved ?? 0, label: "Approved" },
            { icon: Clock, tone: "text-[#FF5F15]", value: fieldCounts?.pending ?? 0, label: "Pending" },
            { icon: XCircle, tone: "text-red-500", value: fieldCounts?.rejected ?? 0, label: "Rejected" },
          ].map((s, i) => (
            <div key={i} className="flex flex-col items-center gap-1 px-1" data-testid={`ts-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center gap-1.5">
                <s.icon className={`w-4 h-4 ${s.tone}`} strokeWidth={2.2} />
                <span className="font-display font-black text-2xl leading-none text-white">{s.value}</span>
              </div>
              <span className="text-[8.5px] font-bold uppercase tracking-widest text-zinc-500 text-center leading-tight">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Field worker status filter pills */}
      {!isOffice && (
        <div className="mb-5 grid grid-cols-4 gap-1 bg-zinc-100 dark:bg-[#101620] border border-zinc-200 dark:border-zinc-800 rounded-xl p-1" data-testid="field-filter-pills">
          {["all", "pending", "approved", "rejected"].map((s) => (
            <button key={s} type="button" onClick={() => setFilter(s)}
                    className={`h-9 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors ${
                      filter === s
                        ? "bg-[#FF5F15] text-white"
                        : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
                    }`}
                    aria-pressed={filter === s}
                    data-testid={`timesheet-filter-${s}-field`}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      {isOffice && (
        <div className="flex flex-wrap gap-2 mb-5 items-center" data-testid="timesheets-filter-row">
          <div className="relative flex-1 min-w-[220px] max-w-xl">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by worker or job…"
              className="pl-10 h-11 rounded-lg border-zinc-200 dark:border-[#33363B] bg-white dark:bg-[#101620]"
              data-testid="timesheets-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900 dark:hover:text-white" aria-label="Clear search" data-testid="timesheets-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {["all", "pending", "approved", "rejected"].map((s) => (
              <button key={s} type="button"
                className={`h-10 px-4 md:px-5 rounded-lg text-[13px] font-semibold capitalize transition-colors border ${
                  filter === s
                    ? "bg-[#FF5F15] border-[#FF5F15] text-white"
                    : "bg-white dark:bg-[#101620] border-zinc-200 dark:border-[#33363B] text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white hover:border-zinc-300 dark:hover:border-zinc-600"
                }`}
                onClick={() => setFilter(s)}
                data-testid={`timesheet-filter-${s}`}
              >{s}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {(pageData && !busy && !loadError && (debouncedSearch || filter !== "all" || range !== "all")) && (
              <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500" data-testid="timesheets-result-count">
                {pageData?.total||0} matching results
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={`h-10 rounded-lg border-zinc-200 dark:border-[#33363B] bg-white dark:bg-[#101620] font-semibold ${range !== "all" ? "text-[#FF5F15]" : "text-zinc-700 dark:text-zinc-200"}`} data-testid="ts-filters-button" data-tour-id="ts-filters-button">
                  <Filter className="w-4 h-4 mr-2" /> Filters
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-lg border border-zinc-200 dark:border-[#33363B] text-[13px]">
                {[["all", "All time"], ["this_week", "This week"], ["last_week", "Last week"], ["last_30", "Last 30 days"]].map(([k, label]) => (
                  <DropdownMenuItem key={k} onClick={() => setRange(k)} data-testid={`ts-range-${k}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-2 ${range === k ? "bg-[#FF5F15]" : "bg-transparent"}`} />
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs" aria-label="Timesheet warning summary">
        <span>Requiring review: <strong>{pageData?.summary?.warnings?.flagged ?? '—'}</strong></span>
        <span>Duplicates / overlaps: <strong>{pageData?.summary?.warnings?.duplicates ?? '—'}</strong></span>
        <span>Future-dated: <strong>{pageData?.summary?.warnings?.future ?? '—'}</strong></span>
        <label>Review warnings <select className="ml-2 border rounded p-2 bg-white dark:bg-[#101620]" value={warningFilter} onChange={e => {setWarningFilter(e.target.value);setPage(1);}}>
          <option value="all">All</option><option value="flagged">Flagged</option><option value="duplicates">Duplicates</option><option value="future">Future-dated</option>
        </select></label>
      </div>
      {isOffice&&<div className="flex flex-wrap gap-2 mb-3">{[['worker','Worker',workerId,setWorkerId,workers.map(w=>[w.id,w.name])],['job','Job',jobId,setJobId,jobs.map(j=>[j.id,`${j.job_number||''} ${j.title}`])],['type','Type of day',dayType,setDayType,['work','rained_off','sick','annual_leave','public_holiday','bereavement','other_leave'].map(v=>[v,v.replaceAll('_',' ')])]].map(([key,label,value,setter,options])=><label key={key} className="text-xs">{label}<select className="block border rounded p-2 bg-white dark:bg-[#101620] max-w-64" value={value} onChange={e=>{setter(e.target.value);setPage(1);}}><option value="">All</option>{options.map(([id,name])=><option key={id} value={id}>{name}</option>)}</select></label>)}<Button variant="outline" onClick={()=>setRange('custom')}>Custom dates</Button></div>}
      {range==='custom'&&<div className="flex gap-2 mb-3">{[['From',customStart,setCustomStart],['To',customEnd,setCustomEnd]].map(([label,value,setter])=><label key={label} className="text-xs">{label}<Input type="date" value={value} onChange={e=>{setter(e.target.value);setPage(1);}}/></label>)}</div>}
      {!loadError && <div className="flex flex-wrap items-center gap-2 mb-3" aria-live="polite">
        <p className="text-xs mr-auto">{busy?'Loading matching records…':`Showing ${pageData?.total?(pageData.page-1)*pageSize+1:0}–${pageData?Math.min(pageData.page*pageSize,pageData.total):0} of ${pageData?.total||0}`}{!busy && pageData && <> · {pageData.summary.hours} hours across all matching records</>}</p>
        <select aria-label="Timesheets per page" className="border rounded p-2 bg-white dark:bg-[#101620] text-xs" value={pageSize} onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}}>{(isOffice?[25,50,100]:[20,50]).map(n=><option key={n} value={n}>{n} per page</option>)}</select>
        <Button variant="outline" disabled={busy||page<=1} onClick={()=>setPage(p=>p-1)}>Previous</Button><span className="text-xs">{page} / {pageData?.total_pages||1}</span><Button variant="outline" disabled={busy||!pageData||page>=pageData.total_pages} onClick={()=>setPage(p=>p+1)}>Next</Button><Button variant="ghost" disabled={busy} onClick={load}>Refresh</Button>
        <Button variant="ghost" onClick={resetFilters}>Clear filters</Button>
      </div>}
      <p className="text-xs text-zinc-500 mb-3">Matching totals cover {range==='all'?'all dates':range.replaceAll('_',' ')} and the selected filters. Weekly cards above retain their labelled week scope.</p>
      {loadError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-5" data-testid="timesheets-error"><h2 className="font-semibold">Timesheets could not be loaded</h2><p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">{loadError}</p><div className="flex gap-2 mt-3"><Button variant="outline" onClick={load}>Retry</Button><Button variant="ghost" onClick={resetFilters}>Clear filters</Button></div></div>}
      {/* Cards */}
      {loadError ? null : busy || !loaded ? (
        <div className="space-y-3" data-testid="timesheets-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl border border-zinc-200 dark:border-[#1F2937] bg-zinc-50 dark:bg-[#101620] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 p-12 text-center" data-testid="ts-empty-state">
          <div className="font-mono text-sm font-bold uppercase tracking-[0.3em] text-zinc-400">No matching timesheets</div>
          <div className="text-xs text-zinc-500 mt-2 font-mono">Try changing your filters, or add a timesheet.</div>
        </div>
      ) : (
        <>
          {/* Drafts strip (field worker only) */}
          {!isOffice && items.some((t) => t.status === "draft") && (
            <div className="mb-5" data-testid="drafts-strip">
              <div className="flex items-center gap-2 mb-2">
                <Edit3 className="w-4 h-4 text-[#FF5F15]" />
                <h3 className="font-display font-extrabold uppercase tracking-tight text-sm">In progress · drafts</h3>
                <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                  Fill these throughout the day, then submit
                </span>
              </div>
              <div className="space-y-3">
                {items.filter((t) => t.status === "draft").map((t) => (
                  <DraftCard
                    key={t.id}
                    ts={t}
                    onContinue={() => editDraft(t)}
                    onSubmit={() => submitDraft(t.id)}
                    onDelete={() => remove(t.id)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3" data-testid="timesheets-list">
            {isOffice ? (
              <TimesheetTable
                onBillingChange={load}
                reviewing={reviewing}
                rows={items.filter((t) => t.status !== "draft")}
                expanded={expanded}
                onToggle={toggleDetail}
                onApprove={(id) => review(id, "approved")}
                onReject={(id) => openReject(id)}
                onUnreject={(id) => review(id, "pending")}
                onDelete={(id) => remove(id)}
                onReassign={(t) => setReassigning(t)}
                onEdit={editDraft}
              />
            ) : (
              items.filter((t) => t.status !== "draft").map((t) => (
                <TimesheetCard
                  key={t.id}
                  ts={t}
                  isOffice={isOffice}
                  isOwn={user.id === t.user_id}
                  expanded={!!expanded[t.id]}
                  onToggle={() => toggleDetail(t.id)}
                  onApprove={() => review(t.id, "approved")}
                  onReject={() => openReject(t.id)}
                  onResubmit={() => submitDraft(t.id)}
                  onDelete={() => remove(t.id)}
                  onReassign={() => setReassigning(t)}
                  onEdit={() => editDraft(t)}
                />
              ))
            )}
          </div>

          {/* Week total footer (field worker) */}
          {!isOffice && weekStats && (
            <div className="mt-4 bg-white dark:bg-[#101620] border border-zinc-200 dark:border-zinc-800 rounded-2xl px-4 py-3.5 flex items-center gap-3" data-testid="ts-week-total-card">
              <span className="w-10 h-10 shrink-0 rounded-xl bg-[#FF5F15]/10 border border-[#FF5F15]/30 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-[#FF5F15]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold">This week total</div>
                <div className="text-[11px] text-zinc-500 font-mono">{weekStats.my_week_start} – {weekStats.my_week_end}</div>
              </div>
              <div className="flex items-end gap-1 shrink-0">
                <span className="font-display font-black text-2xl leading-none">{(weekStats.my_week_hours ?? 0).toFixed(2)}</span>
                <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-0.5">hrs</span>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
            </div>
          )}
        </>
      )}

      {reassigning && (
        <ReassignTimesheetDialog
          ts={reassigning}
          onClose={() => setReassigning(null)}
          onDone={() => { setReassigning(null); load(); }}
        />
      )}

      {/* Reject with a reason */}
      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o && !rejectingBusy) setRejecting(null); }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reject-ts-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" /> Reject timesheet
            </DialogTitle>
            <DialogDescription>
              Tell the worker why — they'll see this note and can fix &amp; resubmit. You can also un-reject it later if this was a mis-tap.
            </DialogDescription>
          </DialogHeader>
          {rejecting && <p className="text-sm font-medium" data-testid="reject-ts-context">{rejecting.user_name}<br />{prettyTableDate(rejecting.date)} · {Number(rejecting.hours || 0).toFixed(2)} h</p>}
          <Label htmlFor="timesheet-rejection-reason">Reason for rejection</Label>
          <Textarea
            id="timesheet-rejection-reason"
            disabled={rejectingBusy}
            rows={3}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Tell the worker what needs correcting…"
            className="rounded-sm border-zinc-400"
            data-testid="reject-note-input"
            autoFocus
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={rejectingBusy} onClick={() => setRejecting(null)} className="rounded-sm uppercase tracking-wider font-bold" data-testid="reject-cancel-btn">
              Cancel
            </Button>
            <Button
              disabled={rejectingBusy}
              aria-busy={rejectingBusy}
              variant="outline"
              onClick={async () => { if (await review(rejecting.id, "rejected", rejectNote.trim() || null)) setRejecting(null); }}
              className="rounded-sm border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
              data-testid="reject-confirm-btn"
            >
              {rejectingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} {rejectingBusy ? "Rejecting…" : "Reject timesheet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
//  CARD
// ============================================================
function TimesheetCard({ ts, isOffice, isOwn, expanded, onToggle, onApprove, onReject, onResubmit, onDelete, onReassign, onEdit }) {
  const [photoView, setPhotoView] = useState(null);
  const canAct = isOffice && ts.status === "pending";
  const canDelete = (isOffice || isOwn) && !ts.paid;
  const canEdit = isOffice || (isOwn && (ts.status === "draft" || (ts.status === "rejected" && !ts.paid)));

  const dateLabel = (() => {
    try {
      const d = new Date(ts.date + "T00:00:00");
      return `${DAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`.toUpperCase();
    } catch { return ts.date; }
  })();

  const et = ts.entry_type || "work";
  const ENTRY_META = {
    work:           { icon: ts.travel_segments?.length && !ts.work_hours ? Truck : Briefcase, label: `${ts.travel_segments?.length && !ts.work_hours ? "Travel · " : ""}${ts.job_title || "—"}` },
    rained_off:     { emoji: "🌧️", label: "Rained off" },
    sick:           { emoji: "🤒", label: "Sick day" },
    annual_leave:   { emoji: "🏖️", label: "Annual leave" },
    public_holiday: { emoji: "🇳🇿", label: "Public holiday" },
    bereavement:    { emoji: "🕊️", label: "Bereavement" },
    other_leave:    { emoji: "📅", label: "Other leave" },
  };
  const meta = ENTRY_META[et] || ENTRY_META.work;
  const isWork = et === "work";

  const accent = ts.status === "approved" ? "bg-emerald-500" : ts.status === "rejected" ? "bg-red-500" : "bg-[#FF5F15]";
  const statusPill = {
    approved: "border-emerald-500/50 text-emerald-500 bg-emerald-500/10",
    rejected: "border-red-500/50 text-red-500 bg-red-500/10",
    pending: "border-[#FF5F15]/50 text-[#FF5F15] bg-[#FF5F15]/10",
  }[ts.status] || "border-zinc-500/50 text-zinc-400 bg-zinc-500/10";

  const firstAsset = Array.isArray(ts.assets) && ts.assets.length > 0 ? ts.assets[0] : null;
  const AssetIcon = firstAsset ? assetIcon(firstAsset.vehicle_type) : null;
  const recordedPlantHours = (ts.fleet_hours || 0) + (ts.gear_hours_total || 0);

  return (
    <div
      className={`relative overflow-hidden ${ts.warnings?.length ? "!bg-red-50 dark:!bg-red-950/30 border-l-4 !border-l-red-500" : "bg-white dark:bg-[#101620]"} border border-zinc-200 dark:border-zinc-800 rounded-2xl ts-rise ${ts.paid ? "ring-1 ring-emerald-500/40" : ""}`}
      data-testid={`ts-row-${ts.id}`}
    >
      {!!ts.warnings?.length && <div className="px-5 pt-2"><TimesheetWarnings warnings={ts.warnings} detail={expanded} /></div>}
      {/* Status accent strip */}
      <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${accent}`} />

      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left pl-5 pr-4 pt-3.5 pb-1"
        data-testid={`ts-toggle-${ts.id}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{dateLabel}</span>
          <span className={`shrink-0 border text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-md ${statusPill}`}>
            {ts.status}
          </span>
        </div>
        {isOffice && (
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mt-1">{ts.user_name}</div>
        )}
        <div className="flex items-end justify-between gap-3 mt-1">
          <div className="min-w-0">
            <div className="font-display font-black tracking-tight text-xl leading-tight flex items-center gap-1.5">
              {meta.emoji && <span className="text-lg leading-none shrink-0">{meta.emoji}</span>}
              <span className="truncate">
                {isWork && ts.job_number ? `${ts.job_number} – ${meta.label}` : meta.label}
              </span>
            </div>
            {isWork && ts.job_site_address && (
              <div className="flex items-center gap-1 mt-1 text-[12px] text-zinc-500 min-w-0" data-testid={`ts-location-${ts.id}`}>
                <MapPin className="w-3.5 h-3.5 text-[#FF5F15] shrink-0" />
                <span className="truncate">{ts.job_site_address}</span>
              </div>
            )}
          </div>
          <div className="shrink-0 flex items-end gap-1">
            <span className="font-display font-black text-3xl leading-none">{(ts.hours ?? 0).toFixed(2)}</span>
            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-0.5">hrs</span>
          </div>
        </div>
        {(recordedPlantHours > 0 || ts.paid) && (
          <div className="flex items-center justify-end gap-2 mt-1">
            {recordedPlantHours > 0 && (
              <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                <span className="text-[#FF5F15] font-bold">Plant {recordedPlantHours.toFixed(2)} h</span>
              </span>
            )}
            {ts.paid && (
              <span className="inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40 text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md">
                <CheckCircle2 className="w-3 h-3" /> Paid
              </span>
            )}
          </div>
        )}
      </button>

      {/* Meta row — asset · times · kebab */}
      <div className="mx-4 ml-5 mt-1.5 border-t border-zinc-100 dark:border-zinc-800/80 py-2.5 flex items-center gap-3.5 min-w-0">
        {Array.isArray(ts.crew) && ts.crew.length > 0 && (
          <span className="flex -space-x-2.5 shrink-0" data-testid={`ts-crew-${ts.id}`}>
            {ts.crew.slice(0, 3).map((c) => (
              <span key={c.user_id} title={c.name}
                    className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-[#101620] bg-[#09090B] text-white overflow-hidden flex items-center justify-center text-[11px] font-black uppercase shrink-0">
                {c.avatar_path
                  ? <AuthImage path={c.avatar_path} alt={c.name} className="w-full h-full object-cover" />
                  : (c.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </span>
            ))}
            {ts.crew.length > 3 && (
              <span className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-[#101620] bg-zinc-200 dark:bg-[#151C27] text-zinc-600 dark:text-zinc-300 flex items-center justify-center text-[11px] font-bold shrink-0">
                +{ts.crew.length - 3}
              </span>
            )}
          </span>
        )}
        {firstAsset && (
          <span className="inline-flex items-center gap-2 min-w-0 shrink" data-testid={`ts-asset-chip-${ts.id}-${firstAsset.id}`}>
            <span className="w-8 h-8 shrink-0 rounded-lg bg-zinc-100 dark:bg-[#151C27] flex items-center justify-center">
              <AssetIcon className="w-4 h-4 text-zinc-500 dark:text-zinc-300" />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block text-[12px] font-bold truncate">{firstAsset.rego}{ts.assets.length > 1 ? ` +${ts.assets.length - 1}` : ""}</span>
              <span className="block text-[11px] text-zinc-500 capitalize truncate">{firstAsset.vehicle_type || `${firstAsset.make || ""} ${firstAsset.model || ""}`.trim() || "Plant"}</span>
            </span>
          </span>
        )}
        {isWork && (
          <span className="inline-flex items-center gap-2 min-w-0 shrink">
            <span className="w-8 h-8 shrink-0 rounded-lg bg-zinc-100 dark:bg-[#151C27] flex items-center justify-center">
              <Clock className="w-4 h-4 text-zinc-500 dark:text-zinc-300" />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block text-[12px] font-bold whitespace-nowrap">{ts.travel_segments?.length && !ts.work_hours ? ts.travel_segments[0].start_time : ts.start_time} – {ts.travel_segments?.length && !ts.work_hours ? ts.travel_segments[ts.travel_segments.length - 1].end_time : ts.end_time}</span>
              <span className="block text-[11px] text-zinc-500">{ts.break_minutes ? `${ts.break_minutes}m break` : "No break"}</span>
            </span>
          </span>
        )}
        <div className="ml-auto shrink-0 flex items-center gap-1">
          {canAct && (
            <>
              <button onClick={onApprove} title="Approve"
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-emerald-500/40 text-emerald-500 hover:bg-emerald-500 hover:text-white transition-colors"
                data-testid={`approve-ts-${ts.id}`}>
                <Check className="w-4 h-4" strokeWidth={3} />
              </button>
              <button onClick={onReject} title="Reject"
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-red-500/40 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                data-testid={`reject-ts-${ts.id}`}>
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                data-testid={`ts-menu-${ts.id}`} aria-label="Timesheet actions">
                <MoreHorizontal className="w-5 h-5 rotate-90" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg text-[13px]">
              <DropdownMenuItem onClick={onToggle} data-testid={`ts-menu-details-${ts.id}`}>
                <ChevronDown className={`w-3.5 h-3.5 mr-2 transition-transform ${expanded ? "rotate-180" : ""}`} /> {expanded ? "Hide details" : "Details"}
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={onEdit} data-testid={`edit-ts-${ts.id}`}>
                  <FileEdit className="w-3.5 h-3.5 mr-2" /> Edit
                </DropdownMenuItem>
              )}
              {isOffice && ts.status !== "approved" && (
                <DropdownMenuItem onClick={onReassign} data-testid={`reassign-ts-${ts.id}`}>
                  <UserCog className="w-3.5 h-3.5 mr-2" /> Reassign
                </DropdownMenuItem>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600" data-testid={`delete-ts-${ts.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Rejected — show the office's reason + let the worker fix & resubmit */}
      {ts.status === "rejected" && isOwn && !isOffice && !ts.paid && (
        <div className="mx-4 ml-5 mb-3 rounded-xl border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 p-3" data-testid={`ts-rejected-banner-${ts.id}`}>
          <div className="text-[11px] font-bold uppercase tracking-widest text-red-600 dark:text-red-400">Rejected by the office</div>
          {ts.review_notes ? (
            <p className="text-sm text-red-900 dark:text-red-200 mt-1 leading-snug">{ts.review_notes}</p>
          ) : (
            <p className="text-sm text-red-900/70 dark:text-red-200/70 mt-1">No reason given — check with the office if unsure.</p>
          )}
          <div className="flex items-center gap-2 mt-2.5">
            <Button size="sm" onClick={onEdit}
                    className="h-8 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[11px]"
                    data-testid={`fix-resubmit-${ts.id}`}>
              <FileEdit className="w-3.5 h-3.5 mr-1.5" /> Fix &amp; resubmit
            </Button>
            <Button size="sm" variant="outline" onClick={onResubmit}
                    className="h-8 rounded-lg uppercase tracking-wider font-bold text-[11px] border-zinc-400"
                    data-testid={`resubmit-asis-${ts.id}`}>
              <Send className="w-3.5 h-3.5 mr-1.5" /> Resubmit as-is
            </Button>
          </div>
        </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-zinc-200 px-4 sm:px-5 py-4 bg-white" data-testid={`ts-detail-${ts.id}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <DetailCol label="Submitted" value={new Date(ts.created_at).toLocaleString("en-NZ")} mono />
            <DetailCol label="Worker" value={ts.user_name} />
            <DetailCol label={isWork ? "Total Worked" : "Recorded hours"} value={`${(ts.hours ?? 0).toFixed(2)} hours`} />
            <DetailCol label="Status" value={ts.status} />
          </div>

          <div className="mt-3">
            <div className="label-tag mb-2">Time &amp; Pay</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {isWork && (!ts.travel_segments?.length || ts.work_hours > 0) && <><span>Start {ts.start_time}</span><span>Finish {ts.end_time}</span><span>Break {ts.break_minutes || 0} min</span></>}
              <span>{isWork ? "Total Worked" : "Recorded hours"} <b>{(ts.hours ?? 0).toFixed(2)} h</b></span>
            </div>
            {isWork && <p className="mt-1 text-xs text-zinc-500">Paid labour hours</p>}
            {ts.non_billable_hours > 0 && <p className="mt-2 text-xs font-medium">{Number(ts.non_billable_hours).toFixed(2)} h non-billable labour · included in pay and job costs</p>}
            {!!ts.travel_segments?.length && <div className="mt-2 space-y-1 text-xs"><p>Working {(ts.work_hours || 0).toFixed(2)} h · Travel {(ts.travel_hours || 0).toFixed(2)} h · Total payable {(ts.hours || 0).toFixed(2)} h</p>{ts.travel_segments.map((segment, i) => <p key={i}>Travel {segment.start_time}–{segment.end_time} · {(segment.hours || 0).toFixed(2)} h · {segment.from_location} → {segment.to_location}{segment.notes && ` · ${segment.notes}`}</p>)}</div>}
          </div>
          {recordedPlantHours > 0 && <div className="mt-3 text-xs">
            <span>Total Recorded Plant <b>{recordedPlantHours.toFixed(2)} h</b></span>
            <p className="mt-1 text-zinc-500">Plant utilisation is operational information and does not change paid labour hours.</p>
          </div>}

          {/* Hours split across job tasks */}
          {Array.isArray(ts.task_hours_detail) && ts.task_hours_detail.length > 0 && (
            <div className="mt-4" data-testid={`ts-detail-tasks-${ts.id}`}>
              <div className="label-tag mb-2">Hours by task</div>
              <div className="space-y-1.5">
                {ts.task_hours_detail.map((d) => (
                  <div key={d.task_id} className="flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-xs">
                    <ListChecks className="w-3.5 h-3.5 text-[#FF5F15] shrink-0" />
                    <span className="font-bold flex-1 min-w-0 truncate">{d.title}</span>
                    {d.machine_names?.length > 0 && (
                      <span className="text-zinc-500 font-mono truncate">{d.machine_names.join(", ")}</span>
                    )}
                    <span className="font-mono font-bold text-[#FF5F15] shrink-0">{Number(d.hours || 0).toFixed(2)}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assets in expanded view show with photo when available */}
          {Array.isArray(ts.assets) && ts.assets.length > 0 && (
            <div className="mt-4">
              <div className="label-tag mb-2">Plant Used</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ts.assets.map((a) => {
                  const Icon = assetIcon(a.vehicle_type);
                  return (
                    <div key={a.id} className="flex items-center gap-2 border border-zinc-300 bg-zinc-50 p-2" data-testid={`ts-asset-detail-${ts.id}-${a.id}`}>
                      {a.photo_path ? (
                        <AuthImage path={a.photo_path} alt={a.rego} className="w-10 h-10 object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-[#09090B] text-[#FF5F15] flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-sm leading-tight">{a.rego}</div>
                        <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest leading-tight">
                          {a.make} {a.model}
                        </div>
                        {typeof a.hours === "number" && <div className="text-xs font-mono">{a.hours.toFixed(2)} h</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {Array.isArray(ts.gear_items) && ts.gear_items.length > 0 && <div className="mt-4">
            <div className="label-tag mb-2">Equipment Used</div>
            <div className="space-y-1 text-xs">
              {ts.gear_items.map((item) => <div key={item.id}>{item.name}{typeof item.hours === "number" && ` · ${item.hours.toFixed(2)} h`}</div>)}
            </div>
          </div>}

          {ts.notes && (
            <div className="mt-4">
              <div className="label-tag mb-1">Worker notes</div>
              <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{ts.notes}</p>
            </div>
          )}
          {Array.isArray(ts.photo_paths) && ts.photo_paths.length > 0 && (
            <div className="mt-4">
              <div className="label-tag mb-2 flex items-center gap-1.5">
                <Camera className="w-3 h-3" /> Job photos ({ts.photo_paths.length})
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid={`ts-photos-${ts.id}`}>
                {ts.photo_paths.filter((p) => !p.startsWith("offline://")).map((p, i) => (
                  <button key={p} type="button" onClick={() => setPhotoView(i)}
                          className="aspect-square border border-zinc-300 bg-zinc-100 overflow-hidden cursor-zoom-in"
                          data-testid={`ts-photo-open-${ts.id}-${i}`}>
                    <AuthImage path={p} alt="Job photo" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              <PhotoLightbox
                paths={ts.photo_paths.filter((p) => !p.startsWith("offline://"))}
                startIndex={photoView ?? 0}
                open={photoView !== null}
                onClose={() => setPhotoView(null)}
              />
            </div>
          )}
          {(ts.review_notes || ts.reviewed_at) && (
            <div className="mt-4 border-l-2 border-zinc-300 pl-3">
              <div className="label-tag mb-1">Review</div>
              {ts.reviewed_at && <div className="text-xs text-zinc-500 font-mono">Reviewed {new Date(ts.reviewed_at).toLocaleString("en-NZ")}</div>}
              {ts.review_notes && <p className="text-sm text-zinc-800 mt-1">{ts.review_notes}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Office command strip — bento grid of hard-shadow stat blocks with a 7-day
// spark, approval progress and a tap-to-review pending tile.
function OfficePulseStrip({ totals, items, onReviewPending }) {
  const days = useMemo(() => {
    const byDate = {};
    for (const t of items) {
      if (!t.date || t.status === "draft") continue;
      byDate[t.date] = (byDate[t.date] || 0) + (t.hours || 0);
    }
    return Object.entries(byDate).sort((a, b) => a[0].localeCompare(b[0])).slice(-7);
  }, [items]);
  const maxDay = Math.max(1, ...days.map(([, h]) => h));
  const pct = totals.count > 0 ? Math.round((totals.approved / totals.count) * 100) : 0;
  const rejected = Math.max(0, totals.count - totals.approved - totals.pending);
  const box = "relative bg-white dark:bg-[#09090B] border-2 border-zinc-900 dark:border-zinc-700 p-4 shadow-[4px_4px_0_0_#09090B] dark:shadow-[4px_4px_0_0_rgba(255,95,21,0.2)] transition-all duration-150";
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="ts-summary-row">
      {/* Hours + approval progress */}
      <div className={`${box} ts-rise`} style={{ animationDelay: "0ms" }}>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">// Hours</div>
        <div className="flex items-end gap-1.5 mt-1.5">
          <span className="font-display font-black text-4xl leading-none text-[#FF5F15]">{totals.sum.toFixed(1)}</span>
          <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-0.5">hrs</span>
        </div>
        <div className="mt-3">
          <div className="flex justify-between text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
            <span>{totals.approved}/{totals.count} approved</span><span>{pct}%</span>
          </div>
          <div className="h-1.5 bg-zinc-200 dark:bg-zinc-800">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* Entries + status breakdown */}
      <div className={`${box} ts-rise`} style={{ animationDelay: "60ms" }}>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">// Entries</div>
        <div className="font-display font-black text-4xl leading-none mt-1.5 text-zinc-900 dark:text-white">{totals.count}</div>
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono uppercase tracking-widest">
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><span className="w-1.5 h-1.5 bg-emerald-500" />{totals.approved} ok</span>
          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400"><span className="w-1.5 h-1.5 bg-amber-500" />{totals.pending} wait</span>
          {rejected > 0 && <span className="flex items-center gap-1 text-red-600 dark:text-red-400"><span className="w-1.5 h-1.5 bg-red-500" />{rejected} no</span>}
        </div>
      </div>

      {/* Pending — tappable, jumps to the review queue */}
      <button
        type="button"
        onClick={onReviewPending}
        className={`${box} ts-rise text-left cursor-pointer hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#09090B] dark:hover:shadow-[2px_2px_0_0_rgba(255,95,21,0.2)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none`}
        style={{ animationDelay: "120ms" }}
        data-testid="ts-pending-tile"
      >
        <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
          // Pending {totals.pending > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
        </div>
        <div className={`font-display font-black text-4xl leading-none mt-1.5 ${totals.pending > 0 ? "text-amber-500 dark:text-amber-400" : "text-zinc-300 dark:text-zinc-600"}`}>{totals.pending}</div>
        <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1">
          {totals.pending > 0 ? <>Tap to review <ChevronRight className="w-3 h-3 text-[#FF5F15]" /></> : "Queue clear"}
        </div>
      </button>

      {/* 7-day spark */}
      <div className={`${box} ts-rise hidden lg:block`} style={{ animationDelay: "180ms" }}>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">// Last 7 days</div>
        <div className="flex items-end gap-1 h-14 mt-2" data-testid="ts-pulse-spark">
          {days.length === 0 ? (
            <span className="text-[11px] font-mono text-zinc-400 dark:text-zinc-600 uppercase">No data</span>
          ) : days.map(([d, h]) => (
            <div key={d} className="flex-1 h-full flex flex-col items-center justify-end gap-0.5 group/bar" title={`${d} · ${h.toFixed(1)} hrs`}>
              <div className="w-full bg-[#FF5F15] transition-all duration-300 group-hover/bar:bg-[#09090B] dark:group-hover/bar:bg-white" style={{ height: `${Math.max(8, (h / maxDay) * 85)}%`, opacity: 0.55 + 0.45 * (h / maxDay) }} />
              <span className="text-[11px] font-mono text-zinc-400 dark:text-zinc-600">{d.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Date group header for the office list — per-day roll-up.
function DayDivider({ date, rows }) {
  const hrs = rows.reduce((a, t) => a + (t.hours || 0), 0);
  const workers = new Set(rows.map((t) => t.user_id)).size;
  const pending = rows.filter((t) => t.status === "pending").length;
  let label = date;
  try {
    label = new Date(`${date}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });
  } catch { /* keep raw */ }
  return (
    <div className="sticky top-12 z-10 -mx-1 px-1 py-2 bg-zinc-50/90 dark:bg-[#09090B]/90 backdrop-blur-md flex items-center gap-3 mt-7 mb-2 first:mt-0" data-testid={`ts-day-divider-${date}`}>
      <span className="bg-[#09090B] dark:bg-white dark:text-black text-white px-2.5 py-1 text-[11px] font-mono font-bold uppercase tracking-[0.15em]">{label}</span>
      <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
        {rows.length} entr{rows.length === 1 ? "y" : "ies"} · {workers} worker{workers === 1 ? "" : "s"} · {hrs.toFixed(1)} hrs
        {pending > 0 && <span className="text-amber-600 font-bold"> · {pending} to review</span>}
      </span>
      <div className="flex-1 h-px bg-zinc-300 dark:bg-zinc-800" />
    </div>
  );
}

function SummaryTile({ label, value, suffix, accent }) {  return (
    <div className="relative bg-white dark:bg-[#101620] border-2 border-zinc-900 dark:border-zinc-800 p-3 sm:p-4">
      <div className={`absolute top-0 left-0 h-1 w-12 ${accent}`} />
      <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 dark:text-[#FF5F15]" style={{ fontFamily: "'Barlow Condensed', system-ui, sans-serif" }}>// {label}</div>
      <div className="mt-2 flex items-end gap-1.5">
        <span className="font-display font-black text-2xl sm:text-3xl leading-none text-zinc-900 dark:text-white">{value}</span>
        {suffix && <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

const ENTRY_LABELS = {
  rained_off: "🌧️ Rained off",
  sick: "🤒 Sick day",
  annual_leave: "🏖️ Annual leave",
  public_holiday: "🇳🇿 Public holiday",
  bereavement: "🕊️ Bereavement",
  other_leave: "📅 Other leave",
};

const chartTooltipStyle = {
  background: "#101620", border: "1px solid #33363B", borderRadius: 2,
  fontSize: 12, fontFamily: "monospace", color: "#fff",
};

// ============================================================
//  OFFICE — weekly summary row (Simpro-style)
// ============================================================
const SUMMARY_CARD = "bg-white dark:bg-[#101620] border border-zinc-200 dark:border-[#1F2937] rounded-xl";
const PIE_COLORS = ["#FF5F15", "#10B981", "#F59E0B", "#18181B", "#71717A", "#A1A1AA"];

function WeekSummaryRow({ week, missing, onPending, onOpenMissing }) {
  const hasMissing = !!missing;
  return (
    <div className="grid lg:grid-cols-12 gap-4 mb-6" data-testid="ts-summary-row">
      <HoursSummaryCard week={week} onPending={onPending} className={hasMissing ? "lg:col-span-5" : "lg:col-span-7"} />
      <HoursByTaskCard week={week} className={hasMissing ? "lg:col-span-4" : "lg:col-span-5"} />
      {hasMissing && (
        <div className="lg:col-span-3 flex flex-col gap-4">
          <FiledCard data={missing} onOpen={onOpenMissing} />
          <BacklogCard data={missing} onOpen={onOpenMissing} />
        </div>
      )}
    </div>
  );
}

function HoursSummaryCard({ week, onPending, className }) {
  const dark = document.documentElement.classList.contains("dark");
  const ink = dark ? "#E4E4E7" : "#18181B";
  const axis = { fontSize: 10, fontFamily: "monospace", fill: dark ? "#71717A" : "#A1A1AA" };
  const days = week?.days || [];
  return (
    <div className={`${SUMMARY_CARD} p-5 ${className || ""}`} data-testid="ts-chart-trend">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400 dark:text-zinc-500">Hours summary (this week)</span>
        <span className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 whitespace-nowrap">{week?.range_label || ""}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="text-3xl font-extrabold tracking-tight text-zinc-900 dark:text-white font-mono" data-testid="ts-week-hours">
          {(week?.total_hours ?? 0).toLocaleString("en-NZ", { maximumFractionDigits: 1 })}
        </span>
        <span className="text-sm font-semibold text-zinc-400">hrs</span>
      </div>
      <div className="text-[11px] font-mono mt-1 text-zinc-500">
        <span className="font-bold text-zinc-700 dark:text-zinc-200">{week?.approved_count ?? 0}/{week?.total_count ?? 0}</span> approved
        {(week?.pending_count ?? 0) > 0 && (
          <>
            {" · "}
            <button type="button" onClick={onPending} className="text-[#FF5F15] font-bold hover:underline" data-testid="ts-pending-tile">
              {week.pending_count} pending
            </button>
          </>
        )}
      </div>
      <div className="h-36 mt-3">
        {days.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-zinc-400">No hours this week</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={days} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1F2937" : "#F0F0F2"} vertical={false} />
              <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={{ stroke: dark ? "#1F2937" : "#E4E4E7" }} />
              <YAxis tick={axis} tickLine={false} axisLine={false} />
              <ChartTooltip contentStyle={chartTooltipStyle} formatter={(v, n) => [`${v} hrs`, n === "total" ? "All hours" : "Approved"]} />
              <Area type="monotone" dataKey="approved" stroke="#10B981" strokeWidth={2} fill="rgba(16,185,129,0.12)" dot={{ r: 3, fill: "#10B981" }} />
              <Line type="monotone" dataKey="total" stroke={ink} strokeWidth={2} dot={{ r: 3, fill: ink }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="flex items-center gap-4 mt-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Approved</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-zinc-900 dark:bg-zinc-200" /> All hours</span>
      </div>
    </div>
  );
}

function HoursByTaskCard({ week, className }) {
  const dark = document.documentElement.classList.contains("dark");
  const rows = (week?.by_job || []).map((r, i) => ({ ...r, color: r.name === "Other" ? "#A1A1AA" : PIE_COLORS[i % PIE_COLORS.length] }));
  const inkFix = (c) => (dark && c === "#18181B" ? "#E4E4E7" : c);
  return (
    <div className={`${SUMMARY_CARD} p-5 ${className || ""}`} data-testid="ts-chart-tasks">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400 dark:text-zinc-500">Hours by task (this week)</div>
      {rows.length === 0 ? (
        <div className="h-40 flex items-center justify-center text-xs text-zinc-400">No hours this week</div>
      ) : (
        <div className="flex items-center gap-3 mt-3">
          <div className="w-28 h-28 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="hours" nameKey="name" cx="50%" cy="50%" innerRadius={32} outerRadius={54} paddingAngle={2} stroke={dark ? "#101620" : "#fff"}>
                  {rows.map((r, i) => <Cell key={i} fill={inkFix(r.color)} />)}
                </Pie>
                <ChartTooltip contentStyle={chartTooltipStyle} formatter={(v, n) => [`${v} hrs`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`ts-task-row-${i}`}>
                <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: inkFix(r.color) }} />
                <span className="font-semibold truncate flex-1 min-w-0" style={{ color: inkFix(r.color) }}>{r.name}</span>
                <span className="text-zinc-400 font-mono whitespace-nowrap shrink-0">{r.pct}% ({r.hours.toLocaleString("en-NZ")} hrs)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function nzTimeLabel(isoDt) {
  try {
    const [h, m] = String(isoDt).slice(11, 16).split(":").map(Number);
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h >= 12 ? "pm" : "am"}`;
  } catch { return ""; }
}

function FiledCard({ data, onOpen }) {
  const armed = !!data.check_armed_after_5pm;
  const today = data.today || { missing: data.missing || [], breakdown: data.breakdown || {} };
  const count = (today.missing || []).length;
  const b = today.breakdown || {};
  const clear = count === 0;
  const tone = clear
    ? { card: "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/25", label: "text-emerald-700 dark:text-emerald-400", dot: "bg-emerald-500", tag: "All in today" }
    : armed
    ? { card: "bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/25", label: "text-red-700 dark:text-red-400", dot: "bg-red-500", tag: "Missing today" }
    : { card: "bg-amber-50 border-amber-200 dark:bg-amber-400/10 dark:border-amber-400/25", label: "text-amber-700 dark:text-amber-400", dot: "bg-amber-400", tag: "Still to be filed" };
  return (
    <button type="button" onClick={onOpen}
      className={`flex-1 border rounded-xl p-4 text-left flex items-center gap-3 transition-transform hover:-translate-y-0.5 ${tone.card}`}
      data-testid="ts-missing-card">
      <div className="min-w-0 flex-1">
        <div className={`text-[11px] font-bold uppercase tracking-[0.16em] flex items-center gap-1.5 ${tone.label}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tone.dot} ${!clear && !armed ? "animate-pulse" : ""}`} />
          {tone.tag}
        </div>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          <span className="text-2xl font-extrabold leading-none text-zinc-900 dark:text-white font-mono">{count}</span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            of {b.total_field_workers || 0} {clear ? "accounted for" : "haven't logged"}
            {b.on_leave > 0 ? ` · ${b.on_leave} on leave` : ""}
            {b.draft_only > 0 ? ` · ${b.draft_only} drafting` : ""}
          </span>
        </div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mt-1.5">
          Checked {nzTimeLabel(data.now_nz)} · {armed ? "After 5pm — chase up" : "Final check after 5pm"}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
    </button>
  );
}

function BacklogCard({ data, onOpen }) {
  const backlogDays = (data.previous_days || []).filter((d) => (d.missing || []).length > 0);
  const total = backlogDays.reduce((a, d) => a + d.missing.length, 0);
  const clear = total === 0;
  return (
    <button type="button" onClick={onOpen}
      className={`flex-1 border rounded-xl p-4 text-left flex items-center gap-3 transition-transform hover:-translate-y-0.5 ${
        clear ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/25"
              : "bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/25"}`}
      data-testid="ts-missing-backlog">
      <div className="min-w-0 flex-1">
        <div className={`text-[11px] font-bold uppercase tracking-[0.16em] flex items-center gap-1.5 ${clear ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
          {clear ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {clear ? "No backlog" : "Backlog · earlier weekdays"}
        </div>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          <span className="text-2xl font-extrabold leading-none text-zinc-900 dark:text-white font-mono">{total}</span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            {clear ? "All caught up" : `overdue across ${backlogDays.length} day${backlogDays.length === 1 ? "" : "s"} (Sat/Sun skipped)`}
          </span>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
    </button>
  );
}

function MissingDetailDialog({ open, onClose, data }) {
  const today = data.today || { missing: data.missing || [], breakdown: data.breakdown || {}, date: data.date };
  const backlog = (data.previous_days || []).filter((d) => (d.missing || []).length > 0);
  const pretty = (iso) => {
    try { const d = new Date(iso + "T00:00:00"); return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`; } catch { return iso; }
  };
  const Chip = ({ u, red }) => (
    <span className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 bg-white dark:bg-[#151C27] ${red ? "border-red-200 dark:border-red-500/30" : "border-amber-200 dark:border-amber-400/30"}`} data-testid={`ts-missing-${u.user_id}`}>
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold uppercase text-white ${red ? "bg-red-500" : "bg-amber-400"}`}>
        {(u.name || u.email || "?").trim().slice(0, 2).toUpperCase()}
      </span>
      <span className="text-[11px] font-semibold truncate max-w-[150px]">{u.name || u.email}</span>
    </span>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-xl max-w-lg" data-testid="ts-missing-dialog">
        <DialogHeader>
          <DialogTitle>Missing timesheets</DialogTitle>
          <DialogDescription className="text-xs">Who still owes hours — today plus earlier weekdays (Sat/Sun skipped).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400 mb-2">Today · {pretty(today.date)}</div>
            {(today.missing || []).length === 0 ? (
              <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> All hours in for today</div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="ts-missing-list">{today.missing.map((u) => <Chip key={u.user_id} u={u} />)}</div>
            )}
          </div>
          {backlog.map((d) => (
            <div key={d.date} data-testid={`ts-missing-backlog-${d.date}`}>
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-red-500 mb-2">{pretty(d.date)} · {d.missing.length} missing</div>
              <div className="flex flex-wrap gap-1.5">{d.missing.map((u) => <Chip key={u.user_id} u={u} red />)}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Yardbook-style compact table — one row per timesheet, click to expand.
function TimesheetTable({ rows, reviewing, expanded, onToggle, onApprove, onReject, onUnreject, onDelete, onReassign, onEdit, onBillingChange }) {
  const th = "px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400 dark:text-zinc-500 whitespace-nowrap";
  return (
    <div className="bg-white dark:bg-[#101620] border border-zinc-200 dark:border-[#1F2937] rounded-xl overflow-x-auto ts-rise ts-inspection-table" data-testid="ts-table">
      <table className="fl-table w-full text-sm min-w-[720px]">
        <thead>
          <tr className="border-b border-zinc-200 dark:border-[#1F2937] text-left">
            <th className={th}>Date</th><th className={th}>Worker</th>

            <th className={th}>Job / work</th><th className={`${th} text-right`}>Hours</th><th className={th}>Status</th>
            <th className={`${th} text-right w-[168px] lg:w-[264px]`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => {
            const isWork = !t.entry_type || t.entry_type === "work";
            const newDay = i > 0 && rows[i - 1].date !== t.date;
            const canAct = t.status === "pending";
            const canEdit = true; // office table — paid entries stay editable for corrections
            const dow = (() => { try { return new Date(`${t.date}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short" }); } catch { return ""; } })();
            const statusChip = {
              approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
              pending: "bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300",
              rejected: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
            }[t.status] || "bg-zinc-100 text-zinc-600 dark:bg-zinc-500/15 dark:text-zinc-300";
            return (
              <Fragment key={t.id}>
                <tr
                  onClick={(event) => {
                    if (!event.target.closest("button, a, input, select, textarea, [role=menuitem]")) onToggle(t.id);
                  }}
                  className={`${t.warnings?.length ? "!bg-red-50 dark:!bg-red-950/30 border-l-4 !border-l-red-500" : ""} cursor-pointer border-b border-zinc-100 dark:border-[#1F2937] hover:bg-zinc-50 dark:hover:bg-[#151C27]/60 transition-colors ${newDay ? "border-t border-t-zinc-200 dark:border-t-[#33363B]" : ""}`}
                  data-testid={`ts-row-${t.id}`}
                >
                  <td className="px-4 py-3.5 text-[13px] whitespace-nowrap text-zinc-700 dark:text-zinc-200">
                    <button type="button" className="inline-flex items-center gap-1.5 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15]"
                      aria-expanded={!!expanded[t.id]} aria-controls={`ts-inspection-${t.id}`}
                      aria-label={`${expanded[t.id] ? "Collapse" : "Expand"} timesheet for ${t.user_name}, ${prettyTableDate(t.date)}`}
                      onClick={(event) => { event.stopPropagation(); onToggle(t.id); }}>
                      <ChevronRight aria-hidden="true" className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${expanded[t.id] ? "rotate-90" : ""}`} />
                      {prettyTableDate(t.date)}
                    </button>
                    <TimesheetWarnings warnings={t.warnings} />
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2.5">
                      <span className="w-7 h-7 shrink-0 rounded-md bg-[#18181B] dark:bg-white dark:text-[#18181B] text-white text-[11px] font-extrabold flex items-center justify-center uppercase">
                        {(t.user_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </span>
                      <span className="font-semibold text-sm text-zinc-900 dark:text-white">{t.user_name}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3.5 max-w-[220px]">
                    {isWork ? (
                      <span className="inline-flex items-center gap-1.5 text-xs truncate max-w-full">
                        <Briefcase className="w-3 h-3 text-zinc-400 shrink-0" />
                        <span className="truncate">{t.travel_segments?.length && !t.work_hours ? "Travel · " : ""}{t.job_title || "—"}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-600 text-amber-800 dark:text-amber-300 text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5">
                        {ENTRY_LABELS[t.entry_type] || t.entry_type}
                      </span>
                    )}
                    {(t.notes || (t.photo_paths || []).length > 0) && (
                      <span className="inline-flex items-center gap-1 ml-1.5 text-zinc-400 align-middle">
                        {t.notes && <FileText className="w-3 h-3" />}
                        {(t.photo_paths || []).length > 0 && <Camera className="w-3 h-3" />}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 font-mono text-sm font-bold text-right whitespace-nowrap">
                    {(t.hours ?? 0).toFixed(2)} h
                    {((t.fleet_hours || 0) > 0 || (t.gear_hours_total || 0) > 0) && (
                      <span title="Plant/equipment usage in hours — not extra worked time" className="block text-[#FF5F15] text-[11px]">Plant {((t.fleet_hours || 0) + (t.gear_hours_total || 0)).toFixed(2)} h</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${statusChip}`}>{t.status}</span>
                    {t.paid && <span className="inline-block ml-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">Paid</span>}
                  </td>
                  <td className="px-4 py-3.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1.5">
                      {canAct && (
                        <>
                          <Button variant="outline" size="sm" disabled={!!reviewing[t.id]} aria-busy={reviewing[t.id] === "approved"}
                            onClick={() => onApprove(t.id)} title="Approve timesheet" aria-label={`Approve timesheet for ${t.user_name} on ${prettyTableDate(t.date)}`}
                            className="h-11 w-11 p-0 lg:h-9 lg:w-auto lg:px-3 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20"
                            data-testid={`approve-ts-${t.id}`}>
                            {reviewing[t.id] === "approved" ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Check aria-hidden="true" />}
                            <span className="hidden lg:inline">{reviewing[t.id] === "approved" ? "Approving…" : "Approve"}</span>
                          </Button>
                          <Button variant="outline" size="sm" disabled={!!reviewing[t.id]}
                            onClick={() => onReject(t.id)} title="Reject timesheet" aria-label={`Reject timesheet for ${t.user_name} on ${prettyTableDate(t.date)}`}
                            className="h-11 w-11 p-0 lg:h-9 lg:w-auto lg:px-3 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                            data-testid={`reject-ts-${t.id}`}>
                            <X aria-hidden="true" /><span className="hidden lg:inline">Reject</span>
                          </Button>
                        </>
                      )}
                      {t.status === "rejected" && !t.paid && (
                        <button disabled={!!reviewing[t.id]} onClick={() => onUnreject(t.id)} title="Un-reject — restore to pending"
                          className="w-8 h-8 rounded-lg flex items-center justify-center border border-amber-300 dark:border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors"
                          data-testid={`unreject-ts-${t.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" strokeWidth={3} />
                        </button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button disabled={!!reviewing[t.id]} title="More actions" className="w-11 h-11 lg:w-9 lg:h-9 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15] disabled:opacity-50 rounded-lg flex items-center justify-center border border-zinc-200 dark:border-[#33363B] text-zinc-500 hover:border-zinc-400 dark:hover:border-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors" aria-label={`More actions for ${t.user_name} on ${t.date}`} data-testid={`ts-actions-${t.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-lg border border-zinc-200 dark:border-[#33363B] text-[13px]">
                          <DropdownMenuItem onClick={() => onToggle(t.id)} data-testid={`ts-toggle-${t.id}`}>
                            <ChevronDown className="w-3.5 h-3.5 mr-2" /> Details
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem onClick={() => onEdit(t)} data-testid={`edit-ts-${t.id}`}>
                              <FileEdit className="w-3.5 h-3.5 mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          {t.status !== "approved" && (
                            <DropdownMenuItem onClick={() => onReassign(t)} data-testid={`reassign-ts-${t.id}`}>
                              <UserCog className="w-3.5 h-3.5 mr-2" /> Reassign
                            </DropdownMenuItem>
                          )}
                          {!t.paid && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => onDelete(t.id)} className="text-red-600 focus:text-red-600" data-testid={`delete-ts-${t.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
                {expanded[t.id] && (
                  <tr data-testid={`ts-detail-${t.id}`}>
                    <td colSpan={6} className="p-0 bg-zinc-50 dark:bg-[#151C27]/50 border-y border-zinc-200 dark:border-zinc-800">
                      <div className="flex flex-wrap gap-4 p-4 text-sm"><span>{dow}</span><span>In: {isWork ? (t.travel_segments?.length && !t.work_hours ? t.travel_segments[0].start_time : t.start_time) || "—" : "—"}</span><span>Out: {isWork ? (t.travel_segments?.length && !t.work_hours ? t.travel_segments[t.travel_segments.length-1].end_time : t.end_time) || "—" : "—"}</span></div>
                      <TimesheetInspectionPanel ts={t} onBillingChange={onBillingChange} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DetailCol({ label, value, mono }) {
  if (!value) return null;
  return (
    <div>
      <div className="label-tag mb-1">{label}</div>
      <div className={`${mono ? "font-mono text-xs" : "text-sm"} text-zinc-800`}>{value}</div>
    </div>
  );
}

// ============================================================
//  NEW TIMESHEET FORM
// ============================================================
function NewTimesheetForm({ jobs, fleet, gear = [], initial, initialJobId = "", clockPrefill = null, onDone }) {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const today = nzTodayIso();
  const isEdit = !!initial;
  // Once a timesheet has been through payroll its hours are locked; the office
  // can still correct the job, cost code and notes via a simplified form.
  const paidLock = isEdit && isOffice && !!initial?.paid;

  const [form, setForm] = useState({
    non_billable_hours: 0,
    travel_segments: [],
    date: clockPrefill?.date || today, job_id: initialJobId, start_time: clockPrefill?.start_time || "07:00", end_time: clockPrefill?.end_time || "16:00",
    break_minutes: 30, notes: "", asset_ids: [], asset_hours: {}, asset_end_hours: {},
    gear_ids: [], gear_hours: {},
    task_hours: {}, task_machine_ids: {},
    photo_paths: [],
    on_behalf_of_user_id: "",  // office can pick the worker; default = self
    entry_type: "work",        // sick / annual / public holiday / rained-off / etc.
    requested_hours: 8,        // Worker-requested paid hours for non-work entries
    cost_code_id: "",          // activity cost code (advanced fieldworkers + office)
  });
  const [workers, setWorkers] = useState([]);
  const [costCodes, setCostCodes] = useState([]);   // activity codes for coding hours
  const [ccPhases, setCcPhases] = useState([]);
  const [jobTasks, setJobTasks] = useState([]);      // hour-tracked tasks on the picked job
  const [arrival, setArrival] = useState(null);        // site sign-in prefill hint
  const startTouched = useRef(!!clockPrefill);
  const endTouched = useRef(!!clockPrefill);
  const [busyAction, setBusyAction] = useState(null);  // "draft" | "submit" | null
  // Stable idempotency key for this entry's submit — reused if a poor-reception
  // attempt times out and the offline queue later re-sends it (no duplicate).
  const submitToken = useRef(null);
  const [jobSearch, setJobSearch] = useState("");
  const [gearSearch, setGearSearch] = useState("");
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  useEffect(() => {
    if (isOffice || isEdit) return;
    let current = true;
    setScheduledJobs([]); setScheduleLoading(true); setScheduleError(false);
    api.get("/assignments", { params: { date: form.date } }).then(({data}) => {
      if (current) setScheduledJobs([...new Set(data.filter(item => item.user_id === user.id && item.job_id).map(item => item.job_id))]);
    }).catch(() => { if(current) setScheduleError(true); }).finally(() => { if(current) setScheduleLoading(false); });
    return () => { current = false; };
  }, [form.date, user.id, isOffice, isEdit]);
  const [assetSearch, setAssetSearch] = useState("");
  const [uploads, setUploads] = useState([]); // in-flight photos: {key,url,pct,error}
  const photoBusy = uploads.some((u) => !u.error);
  const photoInputRef = useRef(null);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (!files.length) return;
    const items = files.map((f, i) => ({ key: `${Date.now()}-${i}`, file: f, url: URL.createObjectURL(f), pct: 0 }));
    setUploads((u) => [...u, ...items]);
    await uploadMany(items, "timesheet", {
      onProgress: (key, pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
      onDone: (key, res) => {
        setForm((f) => ({ ...f, photo_paths: [...f.photo_paths, res.path] }));
        setUploads((u) => {
          const it = u.find((x) => x.key === key);
          if (it?.url) URL.revokeObjectURL(it.url);
          return u.filter((x) => x.key !== key);
        });
        if (res.offline) toast.info("Photo saved on this phone — it'll upload when you're back in reception");
      },
      onError: (key, err) => {
        toast.error(err?.response?.data?.detail || "Photo upload failed");
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: true } : x)));
        setTimeout(() => setUploads((u) => {
          const it = u.find((x) => x.key === key);
          if (it?.url) URL.revokeObjectURL(it.url);
          return u.filter((x) => x.key !== key);
        }), 4000);
      },
    });
  };

  const removePhoto = (p) => setForm((f) => ({ ...f, photo_paths: f.photo_paths.filter((x) => x !== p) }));

  // Pull the org's worker list once for the "For worker" dropdown (office only).
  useEffect(() => {
    if (!isOffice) return;
    api.get("/users").then(({ data }) => setWorkers(data || [])).catch(() => {});
  }, [isOffice]);

  // Activity cost codes — advanced fieldworkers + office get the picker so
  // labour/plant hours auto-code against the job's cost code budget.
  const canCode = isOffice || !!user.advanced_field;
  useEffect(() => {
    if (!canCode) return;
    api.get("/cost-codes/picker")
      .then(({ data }) => {
        setCostCodes(data.cost_codes || []);
        setCcPhases(data.phases || []);
      })
      .catch(() => {});
  }, [canCode]);

  // Hour-tracked tasks on the selected job — workers can split hours onto them.
  useEffect(() => {
    if (!form.job_id) { setJobTasks([]); return; }
    api.get(`/jobs/${form.job_id}/tasks`)
      .then(({ data }) => setJobTasks((data.items || []).filter((t) => t.task_type === "hour_tracked")))
      .catch(() => setJobTasks([]));
  }, [form.job_id]);

  useEffect(() => {
    if (initial) {
      setForm({
        non_billable_hours: initial.non_billable_hours || 0,
        travel_segments: initial.travel_segments || [],
        date: initial.date || today,
        job_id: initial.job_id || "",
        start_time: initial.start_time || "07:00",
        end_time: initial.end_time || "16:00",
        break_minutes: initial.break_minutes ?? 30,
        notes: initial.notes || "",
        asset_ids: initial.asset_ids || [],
        asset_hours: initial.asset_hours || {},
        asset_end_hours: initial.asset_end_hours || {},
        gear_ids: initial.gear_ids || [],
        gear_hours: initial.gear_hours || {},
        task_hours: initial.task_hours || {},
        task_machine_ids: initial.task_machine_ids || {},
        photo_paths: initial.photo_paths || [],
        on_behalf_of_user_id: "",
        entry_type: initial.entry_type || "work",
        requested_hours: initial.requested_hours ?? (initial.entry_type && initial.entry_type !== "work" ? (initial.hours ?? 8) : 8),
        cost_code_id: initial.cost_code_id || "",
      });
    }
    // eslint-disable-next-line
  }, [initial]);

  const [timeCategory, setTimeCategory] = useState(initial?.travel_segments?.length && !initial.work_hours ? "travel" : "work");
  const isWork = form.entry_type === "work";
  const travelHours = (form.travel_segments || []).reduce((sum, segment) => {
    const minutes = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
    return sum + (segment.start_time && segment.end_time ? Math.max(0, minutes(segment.end_time) - minutes(segment.start_time)) / 60 : 0);
  }, 0);
  const updateTravel = (index, key, value) => setForm(f => ({ ...f, travel_segments: f.travel_segments.map((segment, i) => i === index ? { ...segment, [key]: value } : segment) }));
  const addTravel = () => setForm(f => ({ ...f, entry_type: "work", ...(!isEdit && !f.travel_segments.length && !startTouched.current && !endTouched.current ? { start_time: "00:00", end_time: "00:00", break_minutes: 0 } : {}), travel_segments: [...f.travel_segments, { start_time: "", end_time: "", from_location: "", to_location: "", notes: "" }] }));

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const appendNotes = (text) => setForm((f) => ({ ...f, notes: f.notes && f.notes.trim() ? `${f.notes.trim()} ${text}` : text }));

  // Prefill start time from the worker's site sign-in (QR or geofence) —
  // only for new field-worker entries, and only until they touch the field.
  useEffect(() => {
    if (isEdit || !isWork || isOffice || timeCategory === "travel") return;
    let cancelled = false;
    api.get("/site-signin/my-arrival", { params: { date: form.date, job_id: form.job_id || undefined } })
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.arrival) { setArrival(null); return; }
        setArrival(data);
        if (!startTouched.current) setForm((f) => ({ ...f, start_time: data.arrival }));
        if (data.departure && !endTouched.current) setForm((f) => ({ ...f, end_time: data.departure }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, form.job_id, isEdit, isWork, isOffice, timeCategory]);

  // Worker hours preview — actual times for real work, requested hours for leave/sick/rain.
  const workerHours = useMemo(() => {
    if (!isWork) {
      const n = Number(form.requested_hours);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    }
    try {
      const [sh, sm] = (form.start_time || "0:0").split(":").map(Number);
      const [eh, em] = (form.end_time || "0:0").split(":").map(Number);
      let endMin = eh * 60 + em;
      const startMin = sh * 60 + sm;
      if (endMin < startMin) endMin += 24 * 60; // overnight / night shift
      const mins = endMin - startMin - (Number(form.break_minutes) || 0);
      return Math.max(0, mins) / 60;
    } catch { return 0; }
  }, [isWork, form.start_time, form.end_time, form.break_minutes, form.requested_hours]);

  const fleetHoursSum = useMemo(
    () => form.asset_ids.reduce((a, id) => a + (Number(form.asset_hours[id]) || 0), 0),
    [form.asset_ids, form.asset_hours]
  );

  // Searchable job list: matches title, job_number, client_name, or site_address
  const selectedJob = useMemo(() => jobs.find((j) => j.id === form.job_id), [jobs, form.job_id]);
  const filteredJobs = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    return jobs.filter((j) => {
      if (!isOffice && j.status === "completed") return false;
      const hay = [j.title, j.job_number, j.client_name, j.site_address]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [jobs, jobSearch, isOffice]);

  useEffect(() => {
    if (!isOffice && !isEdit && selectedJob?.status === "completed") {
      setForm(f => ({ ...f, job_id: "" }));
    }
  }, [isOffice, isEdit, selectedJob]);

  const matchesEquipment = (query, values) => {
    const text = values.filter(Boolean).join(" ").replace(/_/g, " ").toLowerCase();
    return query.trim().toLowerCase().split(/\s+/).filter(Boolean).every(word => text.includes(word));
  };
  const filteredFleet = fleet.filter(v => matchesEquipment(assetSearch, [v.rego, v.name, v.make, v.model, v.vehicle_type, v.fleet_number, v.serial_number]));
  const filteredGear = gear.filter(g => matchesEquipment(gearSearch, [g.name, g.category, g.make, g.model, g.serial_number, g.asset_number]));

  const toggleAsset = (id) => {
    setForm((f) => {
      if (f.asset_ids.includes(id)) {
        const { [id]: _omit, ...rest } = f.asset_hours;
        const { [id]: _omitEnd, ...restEnd } = f.asset_end_hours;
        return { ...f, asset_ids: f.asset_ids.filter((x) => x !== id), asset_hours: rest, asset_end_hours: restEnd };
      }
      // Default new asset hours to current worker hours so adding feels intuitive
      const defaultH = Math.round(workerHours * 100) / 100;
      return { ...f, asset_ids: [...f.asset_ids, id], asset_hours: { ...f.asset_hours, [id]: defaultH } };
    });
  };

  const setAssetHours = (id, value) => {
    setForm((f) => {
      const { [id]: _omitEnd, ...restEnd } = f.asset_end_hours;
      return { ...f, asset_hours: { ...f.asset_hours, [id]: value === "" ? "" : Number(value) }, asset_end_hours: restEnd };
    });
  };
  // Alternative input: end-of-day meter reading → hours = end - machine meter
  const setAssetEndHours = (id, value, meter) => {
    setForm((f) => {
      const next = { ...f.asset_end_hours };
      if (value === "") delete next[id]; else next[id] = Number(value);
      const hours = value === "" ? "" : Math.max(0, Math.round((Number(value) - (Number(meter) || 0)) * 100) / 100);
      return { ...f, asset_end_hours: next, asset_hours: { ...f.asset_hours, [id]: hours } };
    });
  };

  const toggleGear = (id) => {
    setForm((f) => {
      if (f.gear_ids.includes(id)) {
        const { [id]: _omit, ...rest } = f.gear_hours;
        return { ...f, gear_ids: f.gear_ids.filter((x) => x !== id), gear_hours: rest };
      }
      const defaultH = Math.round(workerHours * 100) / 100;
      return { ...f, gear_ids: [...f.gear_ids, id], gear_hours: { ...f.gear_hours, [id]: defaultH } };
    });
  };

  const setGearHours = (id, value) => {
    setForm((f) => ({ ...f, gear_hours: { ...f.gear_hours, [id]: value === "" ? "" : Number(value) } }));
  };

  const toggleTaskHours = (id) => {
    setForm((f) => {
      if (f.task_hours[id] != null) {
        const { [id]: _omit, ...rest } = f.task_hours;
        const { [id]: _omitM, ...restM } = f.task_machine_ids;
        return { ...f, task_hours: rest, task_machine_ids: restM };
      }
      const defaultH = Math.round(workerHours * 100) / 100;
      return { ...f, task_hours: { ...f.task_hours, [id]: defaultH } };
    });
  };

  const setTaskHours = (id, value) => {
    setForm((f) => ({ ...f, task_hours: { ...f.task_hours, [id]: value === "" ? "" : Number(value) } }));
  };

  const toggleTaskMachine = (tid, mid) => {
    setForm((f) => {
      const cur = f.task_machine_ids[tid] || [];
      const next = cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid];
      return { ...f, task_machine_ids: { ...f.task_machine_ids, [tid]: next } };
    });
  };

  // Show open hour-tracked tasks, plus done ones already carrying hours on this entry.
  const trackedTasks = jobTasks.filter((t) => t.status !== "done" || form.task_hours[t.id] != null);

  const payload = () => {
    const job = jobs.find((j) => j.id === form.job_id);
    if (!isOffice && job?.status === "completed" && (!isEdit || initial.job_id !== job.id)) {
      throw new Error("This job is completed. Choose an open job.");
    }
    const ah = {};
    for (const aid of form.asset_ids) {
      const raw = form.asset_hours[aid];
      const n = raw === "" || raw == null ? 0 : Number(raw);
      ah[aid] = Number.isFinite(n) && n >= 0 ? n : 0;
    }
    const gh = {};
    for (const gid of form.gear_ids) {
      const raw = form.gear_hours[gid];
      const n = raw === "" || raw == null ? 0 : Number(raw);
      gh[gid] = Number.isFinite(n) && n >= 0 ? n : 0;
    }
    const th = {};
    const tmi = {};
    for (const tid of Object.keys(form.task_hours)) {
      const raw = form.task_hours[tid];
      const n = raw === "" || raw == null ? 0 : Number(raw);
      if (Number.isFinite(n) && n > 0) {
        th[tid] = n;
        if ((form.task_machine_ids[tid] || []).length) tmi[tid] = form.task_machine_ids[tid];
      }
    }
    const out = {
      ...form,
      ...(!initial?.source ? { civil_allocation_mode: "exclusive_fleet" } : {}),
      non_billable_hours: Number(form.non_billable_hours) || 0,
      break_minutes: Number(form.break_minutes) || 0,
      asset_hours: ah,
      asset_end_hours: Object.fromEntries(
        form.asset_ids
          .map((aid) => [aid, form.asset_end_hours[aid]])
          .filter(([, v]) => v !== "" && v != null && Number(v) > 0)
      ),
      gear_hours: gh,
      task_hours: th,
      task_machine_ids: tmi,
      job_title: job?.title || null,
      job_id: form.job_id || null,
      cost_code_id: form.cost_code_id || null,
    };
    // Non-work entries (sick / annual / public holiday / rained-off / etc.):
    // strip job + assets so they don't accidentally bill against a job, and
    // send the worker-requested paid hours instead of actual start/end times.
    if (form.entry_type !== "work") {
      out.non_billable_hours = 0;
      out.travel_segments = [];
      out.job_id = null;
      out.job_title = null;
      out.cost_code_id = null;
      out.asset_ids = [];
      out.asset_hours = {};
      out.gear_ids = [];
      out.gear_hours = {};
      out.task_hours = {};
      out.task_machine_ids = {};
      out.photo_paths = [];
      const rh = Number(form.requested_hours);
      out.requested_hours = Number.isFinite(rh) && rh >= 0 ? rh : 8;
      // Reset times to keep the schema happy + signal "no actual times worked".
      out.start_time = "00:00";
      out.end_time = "00:00";
      out.break_minutes = 0;
    } else {
      // Real work — don't send requested_hours (backend ignores it anyway).
      delete out.requested_hours;
    }
    // Only office can set on_behalf_of_user_id; backend will reject if a field
    // worker tries. Strip blanks so the default ("creator owns it") still applies.
    if (!isOffice || !out.on_behalf_of_user_id) delete out.on_behalf_of_user_id;
    return out;
  };

  // Field worker fixing their own rejected entry → edits then resubmits.
  const fixingRejected = isEdit && !isOffice && initial.status === "rejected";

  const saveDraft = async () => {
    setBusyAction("draft");
    try {
      if (isEdit && initial.status === "draft") {
        await api.patch(`/timesheets/${initial.id}/edit`, payload());
        toast.success("Draft saved");
      } else if (isEdit && isOffice) {
        // Office is editing an existing pending/approved entry — just save in place.
        await api.patch(`/timesheets/${initial.id}/edit`, payload());
        toast.success("Timesheet updated");
      } else {
        await api.post("/timesheets", { ...payload(), status: "draft" });
        toast.success("Draft started — keep filling it during the day");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Save failed");
    } finally { setBusyAction(null); }
  };

  const submitTimesheet = async (e) => {
    e?.preventDefault?.();
    setBusyAction("submit");
    try {
      if (isEdit && (initial.status === "draft" || fixingRejected)) {
        // Save latest edits, then submit (or resubmit a rejected one)
        await api.patch(`/timesheets/${initial.id}/edit`, payload());
        const res = await api.post(`/timesheets/${initial.id}/submit`);
        const queued = res.data?.offline_queued || res.offline;
        toast.success(queued
          ? "Saved on your phone — we'll submit it automatically once you're back in reception"
          : (fixingRejected ? "Fixed & resubmitted for review" : "Timesheet submitted"));
        onDone(queued ? {} : { submitted: true, id: initial.id, date: form.date, onBehalf: false });
        return;
      } else if (isEdit && isOffice) {
        // Office is editing an already-submitted entry — just save, no re-submit.
        await api.patch(`/timesheets/${initial.id}/edit`, payload());
        toast.success("Timesheet updated");
        onDone();
        return;
      } else {
        if (!submitToken.current) submitToken.current = (globalThis.crypto?.randomUUID?.() || `t-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        const res = await api.post("/timesheets", { ...payload(), status: "pending", client_token: submitToken.current });
        const queued = res.data?.offline_queued || res.offline;
        toast.success(queued
          ? "Saved on your phone — we'll submit it automatically once you're back in reception"
          : "Timesheet submitted");
        // Trigger the mood prompt only when the worker is submitting their OWN
        // entry (not office filing on behalf, and not a poor-reception queue).
        const onBehalf = !!(isOffice && form.on_behalf_of_user_id);
        onDone(queued ? {} : { submitted: true, id: res.data?.id, date: form.date, onBehalf });
        return;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusyAction(null); }
  };

  // Office correcting a paid (through-payroll) timesheet — hours stay locked.
  const savePaidCorrection = async () => {
    setBusyAction("submit");
    try {
      const job = jobs.find((j) => j.id === form.job_id);
      await api.patch(`/timesheets/${initial.id}/edit`, {
        notes: form.notes,
        job_id: form.job_id || null,
        job_title: job?.title || null,
        cost_code_id: form.cost_code_id || null,
      });
      toast.success("Timesheet updated");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusyAction(null); }
  };
  // Distinguish three modes: new entry, continuing your own draft, or office
  // editing an already-submitted entry. The last one needs a different title +
  // button labels because we're not "submitting", we're correcting in place.
  const editingSubmitted = isEdit && initial.status !== "draft" && !fixingRejected;
  const editingTitle = fixingRejected
    ? "Fix & resubmit"
    : editingSubmitted
      ? "Edit timesheet"
      : isEdit ? "Continue draft" : "Log hours";
  const editingDescription = fixingRejected
    ? "The office rejected this entry — correct it below and resubmit for review."
    : editingSubmitted
      ? `Office override — fixing ${initial.user_name || "this worker"}'s ${initial.status} entry. Save when done.${initial.paid ? " Heads up: this entry has already been paid — edits won't change past pay runs." : ""}`
      : "Fill what you know now. Save as draft and add more as the day goes on — submit at the end.";

  // Paid timesheets: hours are locked after payroll. Show a simple form that
  // only lets the office fix the job, cost code and notes.
  if (paidLock) {
    return (
      <DialogContent
        className="rounded-sm max-w-lg max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden"
        data-testid="timesheet-paid-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
            <Lock className="w-5 h-5" /> Edit paid timesheet
          </DialogTitle>
          <DialogDescription>
            {initial.user_name ? `${initial.user_name}'s ` : ""}{initial.date} entry
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-2 rounded-sm border-2 border-amber-400 bg-amber-50 p-3 text-[13px] text-amber-900" data-testid="ts-paid-lock-banner">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <p>This timesheet has been through payroll, so the <b>hours are locked</b>. You can still fix the job, activity code and notes.</p>
        </div>
        <div className="rounded-sm border-2 border-dashed border-zinc-300 bg-zinc-50 p-3" data-testid="ts-paid-hours-summary">
          <span className="label-tag">Paid hours · locked</span>
          <div className="mt-1 font-display font-black text-xl leading-none">
            {Number(initial.hours || 0).toFixed(2)}h{initial.fleet_hours ? <span className="text-sm font-bold text-zinc-500"> · {Number(initial.fleet_hours).toFixed(2)}h plant</span> : null}
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="label-tag">Job</Label>
            {selectedJob ? (
              <div className="mt-2 border-2 border-[#FF5F15] bg-orange-50 p-3 flex items-start justify-between gap-3" data-testid="ts-paid-job-selected">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                    {selectedJob.job_number && <span className="font-mono text-[11px] font-bold uppercase tracking-widest bg-[#09090B] text-white px-1.5 py-0.5 shrink-0">{selectedJob.job_number}</span>}
                    <span className="font-bold text-sm leading-tight break-words min-w-0">{selectedJob.title}</span>
                  </div>
                  {selectedJob.client_name && <div className="text-xs text-zinc-600 mt-1 truncate">{selectedJob.client_name}</div>}
                </div>
                <button type="button" onClick={() => { set("job_id")(""); setJobSearch(""); }} className="text-[11px] font-bold uppercase tracking-wider text-zinc-600 hover:text-[#FF5F15] shrink-0" data-testid="ts-paid-job-clear">Change</button>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input value={jobSearch} onChange={(e) => setJobSearch(e.target.value)} className="rounded-sm border-zinc-400 pl-9 h-11" placeholder="Search by job, client, address or job #" data-testid="ts-paid-job-search" />
                </div>
                <div className="border border-zinc-300 max-h-56 overflow-y-auto divide-y divide-zinc-200" data-testid="ts-paid-job-list">
                  {filteredJobs.length === 0 ? (
                    <div className="p-4 text-center text-xs text-zinc-500">No matches. Try a different keyword.</div>
                  ) : filteredJobs.slice(0, 50).map((j) => (
                    <button key={j.id} type="button" onClick={() => { set("job_id")(j.id); setJobSearch(""); }} className="w-full text-left px-3 py-2.5 hover:bg-orange-50 transition-colors min-w-0" data-testid={`ts-paid-job-option-${j.id}`}>
                      <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                        {j.job_number && <span className="font-mono text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white px-1.5 py-0.5 shrink-0">{j.job_number}</span>}
                        <span className="font-bold text-sm leading-tight break-words min-w-0 flex-1">{j.title}</span>
                      </div>
                      {j.client_name && <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{j.client_name}</div>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {canCode && costCodes.length > 0 && (
            <div data-testid="ts-paid-cost-code-section">
              <Label className="label-tag">Activity code</Label>
              <select value={form.cost_code_id} onChange={(e) => set("cost_code_id")(e.target.value)} className="mt-2 w-full border border-zinc-400 rounded-sm px-3 py-2.5 text-sm bg-white" data-testid="ts-paid-cost-code-select">
                <option value="">No activity code</option>
                {ccPhases.map((ph) => (
                  <optgroup key={ph.code} label={`${ph.code} · ${ph.name}`}>
                    {costCodes.filter((c) => c.phase_code === ph.code).map((c) => (<option key={c.id} value={c.id}>{c.code} · {c.name}</option>))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label className="label-tag">Notes</Label>
              <VoiceNoteButton onTranscript={appendNotes} />
            </div>
            <Textarea rows={3} value={form.notes} onChange={set("notes")} className="mt-2 rounded-sm border-zinc-400" placeholder="What got done? Type or tap Dictate" data-testid="ts-paid-notes-input" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" disabled={!!busyAction} onClick={savePaidCorrection} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold w-full" data-testid="ts-paid-save-button">
            <Save className="w-4 h-4 mr-2" />{busyAction ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent
      className="rounded-sm max-w-lg max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden p-0 top-[calc(env(safe-area-inset-top,0px)+1rem)] sm:top-[50%] translate-y-0 sm:translate-y-[-50%]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      data-testid="timesheet-form-dialog"
    >
      <div className="p-4 sm:p-6 overflow-x-hidden">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
          {isEdit ? <FileEdit className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
          {editingTitle}
        </DialogTitle>
        <DialogDescription>
          {editingDescription}
        </DialogDescription>
      </DialogHeader>
      {clockPrefill && <p className="my-3 rounded-md border border-border bg-card p-3 text-sm" role="status">Clock-out times prefilled: {clockPrefill.start_time}–{clockPrefill.end_time}. Check the job and deduct your actual break before submitting.{clockPrefill.overnight && <strong className="block mt-2 text-orange-700 dark:text-orange-300">This shift crosses midnight. Split it into separate daily entries; the timesheet uses start and end times on the same date.</strong>}</p>}
      <form onSubmit={submitTimesheet} className="space-y-4 min-w-0 max-w-full overflow-x-hidden">
        {isOffice && !isEdit && (
          <div data-testid="ts-for-worker-block">
            <Label className="label-tag">For worker</Label>
            <Select value={form.on_behalf_of_user_id || "__self"} onValueChange={(v) => setForm({ ...form, on_behalf_of_user_id: v === "__self" ? "" : v })}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="ts-for-worker-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__self">Myself ({user.name || user.email})</SelectItem>
                {workers.filter((w) => w.id !== user.id).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name || w.email} <span className="text-zinc-400 text-xs">· {w.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400">
              Logging for someone else? Pick them here so the hours land in their app.
            </div>
          </div>
        )}
        <div>
          <Label className="label-tag">Date</Label>
          <DateField
            value={form.date}
            onChange={(iso) => setForm((f) => ({ ...f, date: iso }))}
            max={today}
            className="mt-2"
            testId="ts-date-input"
          />
          <div className="mt-2 flex gap-2">
            {[["Today", today], ["Yesterday", shiftIsoDay(today, -1)]].map(([label, iso]) => (
              <Button key={label} type="button" size="sm" variant={form.date === iso ? "default" : "outline"}
                className="rounded-sm text-xs" data-testid={`ts-date-${label.toLowerCase()}`}
                onClick={() => setForm((f) => ({ ...f, date: iso }))}>{label}</Button>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-zinc-400">You can't log a day in the future.</p>
        </div>
        {/* Type of day — work vs leave/sick/etc.
            Switching to a non-work type hides the job/asset pickers (no job to bill against). */}
        <div>
          <Label className="label-tag">Type of day</Label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ts-entry-type-picker">
            {[
              { v: "work", label: "Working", icon: "⛏️" },
              ...(initial?.source === "truck_dispatch" ? [] : [{ v: "travel", label: "Travel", icon: <Truck className="w-5 h-5" /> }]),
              { v: "rained_off", label: "Rained off", icon: "🌧️" },
              { v: "sick", label: "Sick", icon: "🤒" },
              { v: "annual_leave", label: "Annual leave", icon: "🏖️" },
              { v: "public_holiday", label: "Public holiday", icon: "🇳🇿" },
              { v: "bereavement", label: "Bereavement", icon: "🕊️" },
            ].map((opt) => {
              const on = opt.v === "travel" ? isWork && timeCategory === "travel" : form.entry_type === opt.v && (opt.v !== "work" || timeCategory === "work");
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => {
                    setTimeCategory(opt.v === "travel" ? "travel" : "work");
                    if (opt.v === "travel") {
                      if (!form.travel_segments.length) addTravel();
                      else set("entry_type")("work");
                    } else set("entry_type")(opt.v);
                  }}
                  className={`text-left px-2.5 py-2 border-2 rounded-sm transition-colors flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${
                    on
                      ? "border-[#FF5F15] bg-[#FF5F15] text-white"
                      : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-400"
                  }`}
                  data-testid={`ts-entry-type-${opt.v}`}
                >
                  <span className="text-base">{opt.icon}</span>
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })}
          </div>
          {!isWork && (
            <div className="mt-3 border-l-4 border-[#FF5F15] bg-orange-50 p-3 text-[12px] leading-relaxed text-zinc-700" data-testid="ts-leave-help">
              <p>
                <b>Pending office confirmation.</b> Enter the paid hours you're
                requesting below — the office will confirm or adjust them when
                they approve. Standard NZ default is 8&nbsp;hours.
                {form.entry_type === "rained_off" && (
                  <> Heads up — <i>some companies don't pay rain days</i>, so your office may set this to 0.</>
                )}
              </p>
            </div>
          )}
        </div>
        {isWork && (
        <div>
          {!isOffice && !isEdit && <div className="mb-3 rounded-md border border-border bg-card p-3"><p className="text-sm font-medium">Use a scheduled job</p><p className="text-xs text-muted-foreground mt-1">Suggestions for {form.date}. Choose the job you actually worked on.</p>{scheduleLoading ? <p className="text-sm mt-2">Loading your schedule…</p> : scheduleError ? <p className="text-sm mt-2">Schedule unavailable. Search for your job below.</p> : <div className="flex flex-wrap gap-2 mt-2">{scheduledJobs.map(id => jobs.find(job => job.id === id && job.status !== "completed")).filter(Boolean).map(job => <Button key={job.id} type="button" variant="outline" className="min-h-11 h-auto whitespace-normal text-left" onClick={() => { set("job_id")(job.id); setJobSearch(""); }} aria-pressed={form.job_id === job.id}>{job.job_number ? `${job.job_number} · ` : ""}{job.title}</Button>)}{!scheduledJobs.some(id => jobs.some(job => job.id === id && job.status !== "completed")) && <p className="text-sm text-muted-foreground">No open jobs scheduled for this date. You can choose another job below.</p>}</div>}</div>}
          <Label className="label-tag">Job</Label>
          {!isOffice && <p className="text-sm text-muted-foreground mt-1">Completed jobs are hidden from the job picker.</p>}
          {selectedJob ? (
            <div
              className="mt-2 border-2 border-[#FF5F15] bg-orange-50 p-3 flex items-start justify-between gap-3"
              data-testid="ts-job-selected"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                  {selectedJob.job_number && (
                    <span className="font-mono text-[11px] font-bold uppercase tracking-widest bg-[#09090B] text-white px-1.5 py-0.5 shrink-0">
                      {selectedJob.job_number}
                    </span>
                  )}
                  <span className="font-bold text-sm leading-tight break-words min-w-0">{selectedJob.title}</span>
                </div>
                {selectedJob.client_name && (
                  <div className="text-xs text-zinc-600 mt-1 truncate">
                    <span className="label-tag mr-1">Client:</span>{selectedJob.client_name}
                  </div>
                )}
                {selectedJob.site_address && (
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">{selectedJob.site_address}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { set("job_id")(""); setJobSearch(""); }}
                className="text-[11px] font-bold uppercase tracking-wider text-zinc-600 hover:text-[#FF5F15] shrink-0"
                data-testid="ts-job-clear"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="mt-2 space-y-2" data-testid="ts-job-picker">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  className="rounded-sm border-zinc-400 pl-9 h-11"
                  placeholder="Search by job, client, address or job #"
                  data-testid="ts-job-search-input"
                />
              </div>
              <div className="border border-zinc-300 max-h-56 overflow-y-auto divide-y divide-zinc-200" data-testid="ts-job-list">
                {filteredJobs.length === 0 ? (
                  <div className="p-4 text-center text-xs text-zinc-500" data-testid="ts-job-list-empty">
                    {jobs.length === 0 ? "No jobs yet — ask the office to create one." : "No matches. Try a different keyword."}
                  </div>
                ) : (
                  filteredJobs.slice(0, 50).map((j) => (
                    <button
                      key={j.id}
                      type="button"
                      onClick={() => { set("job_id")(j.id); setJobSearch(""); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-orange-50 transition-colors min-w-0"
                      data-testid={`ts-job-option-${j.id}`}
                    >
                      <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                        {j.job_number && (
                          <span className="font-mono text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white px-1.5 py-0.5 shrink-0">
                            {j.job_number}
                          </span>
                        )}
                        <span className="font-bold text-sm leading-tight break-words min-w-0 flex-1">{j.title}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap min-w-0">
                        {j.client_name && <span className="truncate">{j.client_name}</span>}
                        {j.client_name && j.site_address && <span className="text-zinc-300">·</span>}
                        {j.site_address && <span className="truncate">{j.site_address}</span>}
                      </div>
                    </button>
                  ))
                )}
                {filteredJobs.length > 50 && (
                  <div className="px-3 py-2 text-[11px] text-zinc-400 text-center bg-zinc-50">
                    Showing first 50 of {filteredJobs.length} — refine your search to see more.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}
        {isWork && form.job_id && canCode && costCodes.length > 0 && (
          <div data-testid="ts-cost-code-section">
            <Label className="label-tag">Activity code <span className="text-zinc-400 normal-case font-normal">— what were you working on?</span></Label>
            <select
              value={form.cost_code_id}
              onChange={(e) => set("cost_code_id")(e.target.value)}
              className="mt-2 w-full border border-zinc-400 rounded-sm px-3 py-2.5 text-sm bg-white"
              data-testid="ts-cost-code-select">
              <option value="">No activity code</option>
              {ccPhases.map((ph) => (
                <optgroup key={ph.code} label={`${ph.code} · ${ph.name}`}>
                  {costCodes.filter((c) => c.phase_code === ph.code).map((c) => (
                    <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {form.cost_code_id && (
              <p className="mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                Your hours code to {costCodes.find((c) => c.id === form.cost_code_id)?.code}-L · machine hours to -P
              </p>
            )}
          </div>
        )}
        {isWork && form.job_id && trackedTasks.length > 0 && (
          <div data-testid="ts-task-hours-section">
            <div className="flex items-center justify-between mb-2">
              <Label className="label-tag">Task hours <span className="text-zinc-400 normal-case font-normal">— log time against job tasks</span></Label>
              {Object.keys(form.task_hours).length > 0 && (
                <span className="text-[11px] font-mono uppercase tracking-widest text-[#FF5F15]" data-testid="ts-task-hours-count">
                  {Object.keys(form.task_hours).length} selected
                </span>
              )}
            </div>
            <div className="space-y-2">
              {trackedTasks.map((t) => {
                const on = form.task_hours[t.id] != null;
                const mids = form.task_machine_ids[t.id] || [];
                return (
                  <div key={t.id} className={`border-2 transition-colors ${on ? "border-[#FF5F15] bg-orange-50" : "border-zinc-300 bg-white"}`} data-testid={`ts-task-block-${t.id}`}>
                    <button type="button" onClick={() => toggleTaskHours(t.id)} className="w-full flex items-center gap-2.5 p-2.5 text-left" data-testid={`ts-task-toggle-${t.id}`}>
                      <span className={`w-5 h-5 shrink-0 border-2 flex items-center justify-center transition-colors ${on ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-400"}`}>
                        {on && <Check className="w-3.5 h-3.5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold leading-tight break-words">{t.title}</span>
                        <span className="block text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                          {(t.logged_hours || 0).toFixed(1)}h logged{t.budget_hours ? ` of ${t.budget_hours}h budget` : ""}
                        </span>
                      </span>
                    </button>
                    {on && (
                      <div className="px-2.5 pb-2.5 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 flex-1">Hours on this task</span>
                          <div className="relative">
                            <Input type="number" step="0.1" min="0"
                                   value={form.task_hours[t.id] ?? ""}
                                   onChange={(e) => setTaskHours(t.id, e.target.value)}
                                   className="rounded-sm border-zinc-400 w-20 h-8 text-right pr-7 text-sm bg-white"
                                   data-testid={`ts-task-hours-input-${t.id}`} />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold uppercase tracking-wider text-zinc-500 pointer-events-none">h</span>
                          </div>
                        </div>
                        {fleet.length > 0 && (
                          <div>
                            <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Machines on this task <span className="normal-case font-normal">(pick any)</span></div>
                            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                              {fleet.map((v) => {
                                const mOn = mids.includes(v.id);
                                return (
                                  <button key={v.id} type="button" onClick={() => toggleTaskMachine(t.id, v.id)}
                                          className={`px-2 py-1 text-[11px] font-bold uppercase tracking-wider border-2 transition-colors ${mOn ? "bg-[#09090B] text-white border-[#09090B]" : "bg-white border-zinc-300 text-zinc-600 hover:border-[#FF5F15]"}`}
                                          data-testid={`ts-task-machine-${t.id}-${v.id}`}>
                                    {v.rego}{mOn && <Check className="w-3 h-3 inline ml-1 text-[#FF5F15]" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {isWork && form.job_id && (canCode || Number(form.non_billable_hours) > 0) && (
          <div className="rounded-lg border border-zinc-200 dark:border-white/10 p-4 space-y-2">
            <label htmlFor="non-billable-hours" className="block text-sm font-semibold">Non-billable working hours</label>
            <Input id="non-billable-hours" type="number" min="0" max={workerHours} step="0.25"
              className="max-w-40" value={form.non_billable_hours}
              disabled={!canCode || !!(initial?.paid || initial?.invoiced_invoice_id || initial?.machine_invoiced_invoice_id)}
              onChange={e => setForm(f => ({ ...f, non_billable_hours: e.target.value }))} />
            <p className="text-xs text-muted-foreground">Of this day’s {workerHours.toFixed(2)} working hours, how many should not be charged to the client? These hours still count toward pay and job costs. Do not add them again to your working time.</p>
            <p className="text-xs text-muted-foreground">This reduces labour billing only. Plant and travel follow their separate billing settings.</p>
          </div>
        )}

        {isWork && initial?.source !== "truck_dispatch" && (timeCategory === "travel" || form.travel_segments.length > 0) && (
          <section className="border-2 border-zinc-300 p-3 space-y-3" data-testid="ts-travel-segments">
            <Label className="label-tag flex items-center gap-2"><Truck className="w-4 h-4" />Travel time</Label>
            <p className="text-xs text-zinc-500">Submit travel on its own using the job selected above. Working can be submitted separately for the same day. The office decides whether to charge the client; no break is deducted from travel.</p>
            {form.travel_segments.map((segment, index) => (
              <div key={index} className="border-t border-zinc-200 pt-3 space-y-2">
                <div className="flex justify-between items-center"><b className="text-xs">Travel {index + 1}</b><Button type="button" variant="ghost" aria-label={`Remove travel ${index + 1}`} onClick={() => setForm(f => ({ ...f, travel_segments: f.travel_segments.filter((_, i) => i !== index) }))}><Trash2 className="w-4 h-4" /></Button></div>
                <div className="grid grid-cols-2 gap-2">
                  {[['start_time', 'Start', 'time'], ['end_time', 'End', 'time'], ['from_location', 'From (optional)', 'text'], ['to_location', 'To (optional)', 'text']].map(([key, label, type]) => (
                    <label key={key} className="text-xs min-w-0">{label}<Input type={type} value={segment[key] || ''} onChange={e => updateTravel(index, key, e.target.value)} className="mt-1 w-full min-w-0" /></label>
                  ))}
                </div>
                <label className="block text-xs">Notes (optional)<Input value={segment.notes || ''} onChange={e => updateTravel(index, 'notes', e.target.value)} className="mt-1" /></label>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addTravel}><Plus className="w-4 h-4 mr-2" />Add travel segment</Button>
            <Button type="button" variant="ghost" onClick={() => setForm(f => ({ ...f, start_time: "00:00", end_time: "00:00", break_minutes: 0 }))}>Travel only (clear working time)</Button>
          </section>
        )}
        {isWork && form.travel_segments.length > 0 && (timeCategory === "work" || workerHours > 0) && <p className="label-tag">Working time — separate from travel</p>}
        {isWork && form.travel_segments.length > 0 && <p className="text-xs">Working {workerHours.toFixed(2)} h · Travel {travelHours.toFixed(2)} h · Total payable {(workerHours + travelHours).toFixed(2)} h. Plant is recorded separately.</p>}
        {isWork ? (
          <div className={timeCategory === "travel" && workerHours === 0 ? "hidden" : "grid grid-cols-3 gap-2 sm:gap-3"}>
            <div className="min-w-0">
              <Label className="label-tag">Start</Label>
              <Input type="time" value={form.start_time} onChange={(e) => { startTouched.current = true; set("start_time")(e); }} className="mt-2 rounded-sm border-zinc-400 min-w-0 w-full" required data-testid="ts-start-input" />
              {arrival && form.start_time === arrival.arrival && (
                <p className="mt-1 text-[11px] font-mono uppercase tracking-widest text-[#FF5F15]" data-testid="ts-start-prefill-hint">
                  From your {arrival.arrival} site sign-in
                </p>
              )}
            </div>
            <div className="min-w-0">
              <Label className="label-tag">End</Label>
              <Input type="time" value={form.end_time} onChange={(e) => { endTouched.current = true; set("end_time")(e); }} className="mt-2 rounded-sm border-zinc-400 min-w-0 w-full" required data-testid="ts-end-input" />
              {arrival?.departure && form.end_time === arrival.departure && (
                <p className="mt-1 text-[11px] font-mono uppercase tracking-widest text-[#FF5F15]" data-testid="ts-end-prefill-hint">
                  From your {arrival.departure} clock-out
                </p>
              )}
            </div>
            <div className="min-w-0">
              <Label className="label-tag">Break (m)</Label>
              <Input type="number" min="0" value={form.break_minutes} onChange={set("break_minutes")} className="mt-2 rounded-sm border-zinc-400 min-w-0 w-full" data-testid="ts-break-input" />
            </div>
          </div>
        ) : (
          <div data-testid="ts-requested-hours-block">
            <Label className="label-tag">Requested paid hours <span className="text-zinc-400 normal-case font-normal">(office confirms)</span></Label>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Input
                type="number"
                step="0.25"
                min="0"
                max="24"
                value={form.requested_hours}
                onChange={(e) => set("requested_hours")(e.target.value)}
                className="rounded-sm border-zinc-400 w-24"
                data-testid="ts-requested-hours-input"
              />
              <span className="text-xs text-zinc-500">hours</span>
              <div className="flex items-center gap-1 ml-auto">
                {[0, 4, 6, 8].map((h) => (
                  <button
                    type="button"
                    key={h}
                    onClick={() => set("requested_hours")(h)}
                    className={`px-2 py-1 text-[11px] font-bold uppercase tracking-widest border-2 rounded-sm transition-colors ${
                      Number(form.requested_hours) === h
                        ? "border-[#FF5F15] bg-[#FF5F15] text-white"
                        : "border-zinc-300 text-zinc-600 hover:border-zinc-400"
                    }`}
                    data-testid={`ts-requested-quick-${h}`}>
                    {h}h
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Hours preview — worker hours pay, fleet hours tracked separately */}
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 px-3 py-3 grid grid-cols-2 gap-2 text-center" data-testid="ts-hours-preview">
          <div className="bg-[#09090B] text-white p-1 -m-1 rounded-sm">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5F15]">
              {isWork ? "Worker hours · payable" : "Paid leave hours"}
            </div>
            <div className="font-display font-black text-xl leading-none mt-1" data-testid="ts-worker-hours">{(workerHours + (isWork ? travelHours : 0)).toFixed(2)}<span className="text-[11px] text-zinc-400 ml-0.5">h</span></div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
              {isWork ? "Plant allocation" : "No fleet on a leave day"}
            </div>
            <div className="font-display font-black text-xl leading-none mt-1" data-testid="ts-fleet-hours">{isWork ? fleetHoursSum.toFixed(2) : "—"}<span className="text-[11px] text-zinc-500 ml-0.5">{isWork ? "h" : ""}</span></div>
          </div>
        </div>

        {isWork && !initial?.source && <p className="text-xs my-2">Labour allocation {Math.max(0, workerHours - fleetHoursSum).toFixed(2)} h · Plant allocation {fleetHoursSum.toFixed(2)} h. Plant replaces site labour; payroll retains all worked time. Small equipment is additional usage.{fleetHoursSum > workerHours && <span role="alert" className="block text-red-600">Plant allocation cannot exceed site worked hours.</span>}</p>}
        {isWork && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="label-tag">Plant Used <span className="text-zinc-400 normal-case font-normal">(optional)</span></Label>
            {form.asset_ids.length > 0 && (
              <span className="text-[11px] font-mono uppercase tracking-widest text-[#FF5F15]" data-testid="ts-asset-count">
                {form.asset_ids.length} selected
              </span>
            )}
          </div>
          {fleet.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-300 p-3 text-xs text-zinc-500 text-center">
              No active fleet items yet. Ask the office to add some in Fleet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="ts-asset-picker">
              {fleet.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    value={assetSearch}
                    onChange={(e) => setAssetSearch(e.target.value)}
                    className="rounded-md border-border pl-9 h-11"
                    aria-label="Search plant"
                    placeholder="Search plant by rego, name, make or type"
                    data-testid="ts-asset-search-input"
                  />
                </div>
              )}
              {!!form.asset_ids.length && <div className="flex flex-wrap gap-2" aria-label="Selected plant">{form.asset_ids.map(id => <Button key={id} type="button" variant="secondary" className="min-h-11 max-w-full h-auto whitespace-normal text-left" onClick={() => toggleAsset(id)} aria-label={`Remove ${fleet.find(v => v.id === id)?.rego || id}`}>{fleet.find(v => v.id === id)?.rego || id}<X className="w-4 h-4 ml-2 shrink-0" /></Button>)}</div>}
              <p className="text-xs text-muted-foreground" role="status">{filteredFleet.length} matching plant · Select all you used</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-0.5">
                {filteredFleet.length === 0 ? (
                  <div className="col-span-full text-xs text-zinc-500 text-center py-4" data-testid="ts-asset-list-empty">
                    No vehicles or machines match &quot;{assetSearch}&quot;.
                  </div>
                ) : filteredFleet.map((v) => {
                  const Icon = assetIcon(v.vehicle_type);
                  const on = form.asset_ids.includes(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => toggleAsset(v.id)}
                      aria-pressed={on}
                      data-testid={`ts-asset-toggle-${v.id}`}
                      className={`text-left min-h-14 p-3 rounded-md border transition-colors flex items-center gap-2 ${
                        on
                          ? "bg-orange-50 dark:bg-orange-950/30 text-foreground border-[#FF5F15]"
                          : "bg-background border-border hover:border-[#FF5F15]"
                      }`}
                    >
                      {v.photo_path ? (
                        <AuthImage path={v.photo_path} alt={v.rego} className="w-8 h-8 object-cover shrink-0" />
                      ) : (
                        <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${on ? "bg-[#FF5F15] text-white" : "bg-zinc-100 text-zinc-700"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold leading-snug break-words">{v.rego || v.name || "Unnamed plant"}</div>
                        <div className={`text-[11px] font-mono leading-tight truncate ${on ? "text-zinc-400" : "text-zinc-500"}`}>
                          {v.make} {v.model}
                        </div>
                      </div>
                      {on && <Check className="w-4 h-4 shrink-0 text-[#FF5F15]" />}
                    </button>
                  );
                })}
              </div>

              {/* Per-asset hours editor */}
              {form.asset_ids.length > 0 && (
                <div className="border-2 border-[#FF5F15] bg-orange-50 p-3 space-y-2" data-testid="asset-hours-editor">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#FF5F15]">
                    <Gauge className="w-3 h-3" /> Hours each piece actually ran
                  </div>
                  <div className="text-[11px] text-zinc-500 -mt-1">
                    Machines: type the end-of-day hour-meter reading — we&apos;ll work out the hours — <b>or</b> type the hours yourself. Vehicle hours are for job costing only and never touch the odometer.
                  </div>
                  {form.asset_ids.map((aid) => {
                    const v = fleet.find((x) => x.id === aid);
                    if (!v) return null;
                    const Icon = assetIcon(v.vehicle_type);
                    const isMachine = meterTypeFor(v.vehicle_type) === "hours";
                    const meter = Number(v.machine_hours || 0);
                    const endSet = isMachine && form.asset_end_hours[aid] !== undefined && form.asset_end_hours[aid] !== "";
                    return (
                      <div key={aid} className="flex flex-wrap items-center gap-2" data-testid={`asset-hours-row-${aid}`}>
                        <Icon className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                        <div className="flex-1 min-w-[120px]">
                          <span className="text-xs font-bold uppercase tracking-wider">{v.rego}</span>
                          <span className="text-[11px] font-mono text-zinc-500 ml-1.5">{v.make} {v.model}</span>
                          {isMachine && <span className="block text-[11px] font-mono text-zinc-400">meter {meter.toLocaleString("en-NZ")}h</span>}
                        </div>
                        {isMachine && (
                        <>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={form.asset_end_hours[aid] ?? ""}
                            onChange={(e) => setAssetEndHours(aid, e.target.value, meter)}
                            placeholder={meter ? String(meter) : "meter"}
                            className="rounded-sm border-zinc-400 w-24 h-8 text-right pr-9 text-sm"
                            data-testid={`asset-end-hours-input-${aid}`}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold uppercase tracking-wider text-zinc-500 pointer-events-none">end</span>
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">or</span>
                        </>
                        )}
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={form.asset_hours[aid] ?? ""}
                            onChange={(e) => setAssetHours(aid, e.target.value)}
                            readOnly={endSet}
                            className={`rounded-sm border-zinc-400 w-20 h-8 text-right pr-7 text-sm ${endSet ? "bg-zinc-100 text-zinc-500" : ""}`}
                            data-testid={`asset-hours-input-${aid}`}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold uppercase tracking-wider text-zinc-500 pointer-events-none">h</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {isWork && gear.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="label-tag">Gear & small equipment used <span className="text-zinc-400 normal-case font-normal">(optional)</span></Label>
            {form.gear_ids.length > 0 && (
              <span className="text-[11px] font-mono uppercase tracking-widest text-[#FF5F15]" data-testid="ts-gear-count">
                {form.gear_ids.length} selected
              </span>
            )}
          </div>
          <div className="space-y-2" data-testid="ts-gear-picker">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input aria-label="Search gear" value={gearSearch} onChange={e => setGearSearch(e.target.value)} placeholder="Search gear by name, category or serial number" className="pl-9 h-11 rounded-md border-border" data-testid="ts-gear-search-input" /></div>
            {!!form.gear_ids.length && <div className="flex flex-wrap gap-2" aria-label="Selected gear">{form.gear_ids.map(id => <Button key={id} type="button" variant="secondary" className="min-h-11 max-w-full h-auto whitespace-normal text-left" onClick={() => toggleGear(id)} aria-label={`Remove ${gear.find(g => g.id === id)?.name || id}`}>{gear.find(g => g.id === id)?.name || id}<X className="w-4 h-4 ml-2 shrink-0" /></Button>)}</div>}
            <p className="text-xs text-muted-foreground" role="status">{filteredGear.length} matching items · Select all you used</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-0.5">
              {!filteredGear.length && <p className="text-sm text-muted-foreground p-3 col-span-full">No gear matches. Try another name or category.</p>}
              {filteredGear.map((g) => {
                const on = form.gear_ids.includes(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => toggleGear(g.id)}
                    aria-pressed={on}
                    data-testid={`ts-gear-toggle-${g.id}`}
                    className={`text-left min-h-14 p-3 rounded-md border transition-colors flex items-center gap-2 ${
                      on
                        ? "bg-orange-50 dark:bg-orange-950/30 text-foreground border-[#FF5F15]"
                        : "bg-background border-border hover:border-[#FF5F15]"
                    }`}
                  >
                    {g.photo_path ? (
                      <AuthImage path={g.photo_path} alt={g.name} className="w-8 h-8 object-cover shrink-0" />
                    ) : (
                      <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${on ? "bg-[#FF5F15] text-white" : "bg-zinc-100 text-zinc-700"}`}>
                        <Wrench className="w-4 h-4" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold leading-snug break-words">{g.name}</div>
                      <div className={`text-[11px] font-mono leading-tight truncate ${on ? "text-zinc-400" : "text-zinc-500"}`}>
                        {(g.category || "").replace(/_/g, " ")}
                      </div>
                    </div>
                    {on && <Check className="w-4 h-4 shrink-0 text-[#FF5F15]" />}
                  </button>
                );
              })}
            </div>

            {/* Per-gear hours editor */}
            {form.gear_ids.length > 0 && (
              <div className="border-2 border-[#FF5F15] bg-orange-50 p-3 space-y-2" data-testid="gear-hours-editor">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#FF5F15]">
                  <Gauge className="w-3 h-3" /> Hours each piece actually ran
                </div>
                {form.gear_ids.map((gid) => {
                  const g = gear.find((x) => x.id === gid);
                  if (!g) return null;
                  return (
                    <div key={gid} className="flex items-center gap-2" data-testid={`gear-hours-row-${gid}`}>
                      <Wrench className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-bold uppercase tracking-wider">{g.name}</span>
                      </div>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={form.gear_hours[gid] ?? ""}
                          onChange={(e) => setGearHours(gid, e.target.value)}
                          className="rounded-sm border-zinc-400 w-20 h-8 text-right pr-7 text-sm"
                          data-testid={`gear-hours-input-${gid}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold uppercase tracking-wider text-zinc-500 pointer-events-none">h</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

        {isWork && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="label-tag">Job photos <span className="text-zinc-400 normal-case font-normal">(optional — added to the job's gallery)</span></Label>
            <Button type="button" size="sm" variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    className="h-8 rounded-sm border-zinc-400 font-bold uppercase tracking-wider text-[11px]"
                    data-testid="ts-add-photo-btn">
              <Camera className="w-3.5 h-3.5 mr-1.5" /> {photoBusy ? `Uploading ${uploads.length}…` : "Add photos"}
            </Button>
            <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden"
                   onChange={onPickPhotos} data-testid="ts-photo-input" />
          </div>
          {(form.photo_paths.length > 0 || uploads.length > 0) && (
            <div className="grid grid-cols-4 gap-2" data-testid="ts-photo-thumbs">
              {form.photo_paths.map((p) => (
                <div key={p} className="relative aspect-square border border-zinc-300 bg-zinc-100 overflow-hidden">
                  {p.startsWith("offline://") ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 gap-1">
                      <Camera className="w-4 h-4" />
                      <span className="text-[11px] font-bold uppercase tracking-wider">Saved offline</span>
                    </div>
                  ) : (
                    <AuthImage path={p} alt="Job photo" className="w-full h-full object-cover" />
                  )}
                  <button type="button" onClick={() => removePhoto(p)}
                          className="absolute top-0.5 right-0.5 p-1 bg-black/60 text-white hover:bg-red-600"
                          data-testid={`ts-photo-remove-${p}`} aria-label="Remove photo">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {uploads.map((u) => <UploadingThumb key={u.key} up={u} />)}
            </div>
          )}
        </div>
        )}

        {isWork && form.job_id && <TimesheetMaterials key={`${form.job_id}:${form.date}`} jobId={form.job_id} date={form.date} timesheetId={initial?.job_id === form.job_id ? initial.id : undefined} />}

        <div>
          <div className="flex items-center justify-between gap-2">
            <Label className="label-tag">Notes</Label>
            <VoiceNoteButton onTranscript={appendNotes} />
          </div>
          <Textarea rows={3} value={form.notes} onChange={set("notes")} className="mt-2 rounded-sm border-zinc-400" placeholder="What got done? Type or tap Dictate" data-testid="ts-notes-input" />
        </div>
        <DialogFooter
          className="gap-2 flex-col sm:flex-row sm:justify-between sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 pt-3 pb-4 bg-background border-t border-zinc-200 dark:border-zinc-800 z-10"
        >
          {editingSubmitted ? (
            <Button type="submit" disabled={!!busyAction}
                    className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold w-full"
                    data-testid="ts-update-button">
              <Save className="w-4 h-4 mr-2" />
              {busyAction ? "Saving…" : "Save changes"}
            </Button>
          ) : (
            <>
              {!fixingRejected && (
              <Button
                type="button"
                variant="outline"
                onClick={saveDraft}
                disabled={!!busyAction}
                className="rounded-sm uppercase tracking-wider font-bold border-zinc-400 w-full sm:w-auto"
                data-testid="ts-save-draft-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {busyAction === "draft" ? "Saving…" : (isEdit ? "Save draft" : "Save as draft")}
              </Button>
              )}
              <Button type="submit" disabled={!!busyAction}
                      className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold w-full sm:w-auto"
                      data-testid="ts-submit-button">
                <Send className="w-4 h-4 mr-2" />
                {busyAction === "submit" ? "Submitting…" : fixingRejected ? "Fix & resubmit" : (isEdit ? "Submit timesheet" : "Submit hours")}
              </Button>
            </>
          )}
        </DialogFooter>
      </form>
      </div>
    </DialogContent>
  );
}


// ============================================================
//  DRAFT CARD (field worker only)
// ============================================================
function DraftCard({ ts, onContinue, onSubmit, onDelete }) {
  const { day, month, dow } = dateBits(ts.date);
  return (
    <div
      className="relative bg-orange-50 border-2 border-[#FF5F15] border-dashed shadow-[3px_3px_0_0_#FF5F15]"
      data-testid={`ts-draft-${ts.id}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#FF5F15]" />
      <div className="p-4 flex items-stretch gap-4">
        <div className="shrink-0 w-16 text-center border-r border-dashed border-zinc-300 pr-3">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5F15]">{dow}</div>
          <div className="font-display font-black text-3xl leading-none mt-0.5">{day}</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{month}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#FF5F15] bg-white border border-[#FF5F15] px-1.5 py-0.5">
              <Edit3 className="w-3 h-3 inline mr-0.5" /> Draft
            </span>
            <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
              Fill as you go · submit when you're done
            </span>
          </div>
          <div className="font-display font-extrabold uppercase tracking-tight text-base leading-tight flex items-center gap-1.5">
            <Briefcase className="w-4 h-4 text-zinc-400 shrink-0" />
            <span className="truncate">{ts.job_title || "— no job set —"}</span>
          </div>
          <div className="mt-1 text-xs text-zinc-700 font-mono flex items-center gap-3 flex-wrap">
            <span><Clock className="w-3 h-3 inline mr-1" />{ts.travel_segments?.length && !ts.work_hours ? `Travel ${ts.travel_segments[0].start_time}–${ts.travel_segments[ts.travel_segments.length - 1].end_time}` : `${ts.start_time}–${ts.end_time}`}</span>
            <span>·</span>
            <span><b>{(ts.hours || 0).toFixed(2)}h</b> worker</span>
            {(ts.fleet_hours || 0) > 0 && (
              <>
                <span>·</span>
                <span><b className="text-[#FF5F15]">{(ts.fleet_hours || 0).toFixed(2)}h</b> recorded plant</span>
              </>
            )}
          </div>
          {Array.isArray(ts.assets) && ts.assets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {ts.assets.map((a) => (
                <span key={a.id} className="text-[11px] font-bold uppercase tracking-wider bg-white border border-zinc-300 px-1.5 py-0.5">
                  {a.rego}{typeof a.hours === "number" && a.hours > 0 && <span className="text-[#FF5F15] font-mono ml-1">{a.hours.toFixed(1)}h</span>}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(ts.task_hours_detail) && ts.task_hours_detail.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {ts.task_hours_detail.map((d) => (
                <span key={d.task_id} className="text-[11px] font-bold bg-white border border-[#FF5F15]/40 px-1.5 py-0.5">
                  {d.title}<span className="text-[#FF5F15] font-mono ml-1">{Number(d.hours || 0).toFixed(1)}h</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-[#FF5F15]/30 px-3 py-2 flex items-center justify-between gap-2 flex-wrap bg-white">
        <Button size="sm" variant="ghost" onClick={onDelete}
                className="rounded-sm text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:text-red-600"
                data-testid={`draft-delete-${ts.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
        </Button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onContinue}
                  className="rounded-sm h-8 border-zinc-400 text-xs font-bold uppercase tracking-wider"
                  data-testid={`draft-continue-${ts.id}`}>
            <FileEdit className="w-3.5 h-3.5 mr-1" /> Continue
          </Button>
          <Button size="sm" onClick={onSubmit}
                  className="rounded-sm h-8 bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold uppercase tracking-wider"
                  data-testid={`draft-submit-${ts.id}`}>
            <Send className="w-3.5 h-3.5 mr-1" /> Submit
          </Button>
        </div>
      </div>
    </div>
  );
}


// --- Reassign timesheet (office-only fix for "wrong account logged it") ---
function ReassignTimesheetDialog({ ts, onClose, onDone }) {
  const [workers, setWorkers] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then(({ data }) => setWorkers(data || [])).catch(() => {});
  }, []);

  const save = async () => {
    if (!target) { toast.error("Pick a worker"); return; }
    if (target === ts.user_id) { toast.error("Already assigned to that worker"); onClose(); return; }
    setBusy(true);
    try {
      await api.patch(`/timesheets/${ts.id}/reassign`, { user_id: target });
      toast.success("Timesheet reassigned");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassign failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="reassign-ts-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">Reassign timesheet</DialogTitle>
          <DialogDescription className="text-xs">
            Move this {ts.date} timesheet from <strong>{ts.user_name || "unknown"}</strong> to a different worker. Use this when hours were logged on the wrong account (e.g. admin profile on a phone instead of the field worker's own account).
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs">
            <div className="flex items-center justify-between"><span className="text-zinc-500 uppercase tracking-widest text-[11px] font-bold">Currently belongs to</span></div>
            <div className="font-bold mt-1">{ts.user_name || "—"}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">{ts.user_id}</div>
          </div>
          <div>
            <Label className="label-tag">Move to</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="reassign-target-select">
                <SelectValue placeholder="Pick the correct worker…" />
              </SelectTrigger>
              <SelectContent>
                {workers.filter((w) => w.id !== ts.user_id).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name || w.email} <span className="text-zinc-400 text-xs">· {w.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {ts.status === "approved" && (
            <div className="border-l-2 border-red-400 bg-red-50 px-3 py-2 text-[11px] text-red-900">
              Approved timesheets can't be reassigned — they're locked for payroll integrity.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Cancel</Button>
          <Button onClick={save} disabled={busy || !target || ts.status === "approved"} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="reassign-save-btn">
            {busy ? "Moving…" : "Reassign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
